import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, getDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../hooks/useAuth';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import {
  MOTOR_TYPES,
  DIRECTION_TYPES,
  DIKEY_DIRECTION_TYPES,
  TUL_PILE_MODELS,
  KANAT_PILE_MODELS,
  JALUZI_SISTEM_TIPLERI,
  CURRENCY_SYMBOL
} from '../../constants';
import { useTranslation } from 'react-i18next';
import { useLocalizedConstants } from '../../i18n/useLocalizedConstants';
import { formatPrice, calculatePileDensity, convertProductToKztForOrder, convertMechanismToKztForOrder, resolveJaluziOrderPricing } from '../../utils';
import { useExchangeRates } from '../../hooks/useExchangeRates';
import { sendSingleOrderWhatsAppMessage } from '../../utils/whatsappUtils';
import './OrderForm.css';

const OrderForm = ({
  isAddingOrder,
  editingOrderId,
  selectedProductType,
  newOrder,
  isLoading,
  customerInfo,
  customerId,
  onProductTypeChange,
  onFieldChange,
  onSubmit,
  onCancel,
  pileModels = [], // Pile modelleri prop olarak gelebilir (OrderDetails'dan)
  isWholesale = false // Bayi modu: toptan fiyat kullan
}) => {
  const { t } = useTranslation();
  const of = useCallback((key, options) => t(`orders.form.${key}`, options), [t]);
  const { user } = useAuth();
  const {
    PRODUCT_TYPES: localizedProductTypes,
    MOTOR_TYPES: localizedMotorTypes,
    DIRECTION_TYPES: localizedDirectionTypes,
    DIKEY_DIRECTION_TYPES: localizedDikeyDirectionTypes,
    TUL_PILE_MODELS: localizedTulPileModels,
    KANAT_PILE_MODELS: localizedKanatPileModels,
    JALUZI_SISTEM_TIPLERI: localizedJaluziSistemTipleri
  } = useLocalizedConstants();
  const [selectedFirma, setSelectedFirma] = useState('');
  const [selectedFirmaKumas, setSelectedFirmaKumas] = useState('');
  const [selectedFirmaMekanizma, setSelectedFirmaMekanizma] = useState('');
  const [selectedFirmaMotor, setSelectedFirmaMotor] = useState('');
  const [selectedFirmaAksesuar, setSelectedFirmaAksesuar] = useState('');

  const [lastSentFirma, setLastSentFirma] = useState(null);
  const [lastSentFirmaKumas, setLastSentFirmaKumas] = useState(null);
  const [lastSentFirmaMekanizma, setLastSentFirmaMekanizma] = useState(null);
  const [lastSentFirmaMotor, setLastSentFirmaMotor] = useState(null);
  const [lastSentFirmaAksesuar, setLastSentFirmaAksesuar] = useState(null);
  const [whatsappMessageLang, setWhatsappMessageLang] = useState('tr');
  const [companies, setCompanies] = useState([]);
  const [localPileModels, setLocalPileModels] = useState([]);
  const [productItems, setProductItems] = useState([]);
  const [showExtraCost, setShowExtraCost] = useState(false);
  const [showBaseCost, setShowBaseCost] = useState(false);
  const [showMountingCost, setShowMountingCost] = useState(false);

  // Sayısal alanlarda negatif işareti ve 'e' karakterini engelle
  const handleNumericKeyDown = (e) => {
    if (e.key === '-' || e.key === 'e' || e.key === 'E' || e.key === '+') {
      e.preventDefault();
    }
  };

  const [adminSettings, setAdminSettings] = useState({
    sPilePrice: 0, sPileWholesale: 0, sPileCost: 0, american2Price: 0, american2Wholesale: 0, american2Cost: 0, american3Price: 0, american3Wholesale: 0, american3Cost: 0,
    kapsulPrice: 0, kapsulWholesale: 0, kapsulCost: 0,
    motorCurrency: 'USD', zigilPrice: 0, zigilWholesale: 0, zigilCost: 0, reduktorPrice: 0, reduktorWholesale: 0, reduktorCost: 0, kurdelaPrice: 0, kurdelaWholesale: 0, kurdelaCost: 0,
    metalZigilPrice: 0, metalZigilWholesale: 0, metalZigilCost: 0, metalReduktorPrice: 0, metalReduktorWholesale: 0, metalReduktorCost: 0, ikiCamArasiPrice: 0, ikiCamArasiWholesale: 0, ikiCamArasiCost: 0,
    storJumboPrice: 0, storJumboWholesale: 0, storJumboCost: 0, storAltEtekDilimPrice: 0, storAltEtekDilimWholesale: 0, storAltEtekDilimCost: 0, storKapaliKasaPrice: 0, storKapaliKasaWholesale: 0, storKapaliKasaCost: 0,
    storAltEtekLazerPrice: 0, storAltEtekLazerWholesale: 0, storAltEtekLazerCost: 0, storDekoratifLamaPrice: 0, storDekoratifLamaWholesale: 0, storDekoratifLamaCost: 0, storDekoratifKasaPrice: 0, storDekoratifKasaWholesale: 0, storDekoratifKasaCost: 0,
    storAltEtekBeyazIsPrice: 0, storAltEtekBeyazIsWholesale: 0, storAltEtekBeyazIsCost: 0,
    zebraJumboPrice: 0, zebraJumboWholesale: 0, zebraJumboCost: 0, zebraAltEtekDilimPrice: 0, zebraAltEtekDilimWholesale: 0, zebraAltEtekDilimCost: 0, zebraKapaliKasaPrice: 0, zebraKapaliKasaWholesale: 0, zebraKapaliKasaCost: 0,
    dikeyFonPrice: 0, dikeyFonWholesale: 0, dikeyFonCost: 0, dikeyFonList: [],
    katlamaliMekanizmaList: [],
    rustikAyakModelList: [],
    rustikAyakCapList: [],
    rustikAyakUzunlukList: [],
    rustikAyakRenkKoduList: [],
    // Per-product motor settings
    storMotorSettings: { cost: 0, retail: 0, wholesale: 0, currency: 'USD', specialRateRetail: 0, specialRateWholesale: 0 },
    zebraMotorSettings: { cost: 0, retail: 0, wholesale: 0, currency: 'USD', specialRateRetail: 0, specialRateWholesale: 0 },
    ahsapJaluziMotorSettings: { cost: 0, retail: 0, wholesale: 0, currency: 'USD', specialRateRetail: 0, specialRateWholesale: 0 },
    metalJaluziMotorSettings: { cost: 0, retail: 0, wholesale: 0, currency: 'USD', specialRateRetail: 0, specialRateWholesale: 0 },
    dikeyPerdeMotorSettings: { cost: 0, retail: 0, wholesale: 0, currency: 'USD', specialRateRetail: 0, specialRateWholesale: 0 },
    dikeyPvcMotorSettings: { cost: 0, retail: 0, wholesale: 0, currency: 'USD', specialRateRetail: 0, specialRateWholesale: 0 },
    dikeyStorMotorSettings: { cost: 0, retail: 0, wholesale: 0, currency: 'USD', specialRateRetail: 0, specialRateWholesale: 0 },
    katlamaliPerdeMotorSettings: { cost: 0, retail: 0, wholesale: 0, currency: 'USD', specialRateRetail: 0, specialRateWholesale: 0 }
  });
  const exchangeRates = useExchangeRates({ formatForDisplay: true });

  // Ayarları Çek
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settingsRef = doc(db, 'settings', 'global');
        const docSnap = await getDoc(settingsRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setAdminSettings({
            sPilePrice: Number(data.sPilePrice) || 0,
            sPileWholesale: Number(data.sPileWholesale) || 0,
            sPileCost: Number(data.sPileCost) || 0,
            american2Price: Number(data.american2Price) || 0,
            american2Wholesale: Number(data.american2Wholesale) || 0,
            american2Cost: Number(data.american2Cost) || 0,
            american3Price: Number(data.american3Price) || 0,
            american3Wholesale: Number(data.american3Wholesale) || 0,
            american3Cost: Number(data.american3Cost) || 0,
            kapsulPrice: Number(data.kapsulPrice) || 0,
            kapsulWholesale: Number(data.kapsulWholesale) || 0,
            kapsulCost: Number(data.kapsulCost) || 0,
            motorCurrency: data.motorCurrency || 'USD',
            zigilPrice: Number(data.zigilPrice) || 0,
            zigilWholesale: Number(data.zigilWholesale) || 0,
            zigilCost: Number(data.zigilCost) || 0,
            reduktorPrice: Number(data.reduktorPrice) || 0,
            reduktorWholesale: Number(data.reduktorWholesale) || 0,
            reduktorCost: Number(data.reduktorCost) || 0,
            kurdelaPrice: Number(data.kurdelaPrice) || 0,
            kurdelaWholesale: Number(data.kurdelaWholesale) || 0,
            kurdelaCost: Number(data.kurdelaCost) || 0,
            metalZigilPrice: Number(data.metalZigilPrice) || 0,
            metalZigilWholesale: Number(data.metalZigilWholesale) || 0,
            metalZigilCost: Number(data.metalZigilCost) || 0,
            metalReduktorPrice: Number(data.metalReduktorPrice) || 0,
            metalReduktorWholesale: Number(data.metalReduktorWholesale) || 0,
            metalReduktorCost: Number(data.metalReduktorCost) || 0,
            ikiCamArasiPrice: Number(data.ikiCamArasiPrice) || 0,
            ikiCamArasiWholesale: Number(data.ikiCamArasiWholesale) || 0,
            ikiCamArasiCost: Number(data.ikiCamArasiCost) || 0,
            storJumboPrice: Number(data.storJumboPrice) || 0,
            storJumboWholesale: Number(data.storJumboWholesale) || 0,
            storJumboCost: Number(data.storJumboCost) || 0,
            storAltEtekDilimPrice: Number(data.storAltEtekDilimPrice) || 0,
            storAltEtekDilimWholesale: Number(data.storAltEtekDilimWholesale) || 0,
            storAltEtekDilimCost: Number(data.storAltEtekDilimCost) || 0,
            storKapaliKasaPrice: Number(data.storKapaliKasaPrice) || 0,
            storKapaliKasaWholesale: Number(data.storKapaliKasaWholesale) || 0,
            storKapaliKasaCost: Number(data.storKapaliKasaCost) || 0,
            storAltEtekLazerPrice: Number(data.storAltEtekLazerPrice) || 0,
            storAltEtekLazerWholesale: Number(data.storAltEtekLazerWholesale) || 0,
            storAltEtekLazerCost: Number(data.storAltEtekLazerCost) || 0,
            storDekoratifLamaPrice: Number(data.storDekoratifLamaPrice) || 0,
            storDekoratifLamaWholesale: Number(data.storDekoratifLamaWholesale) || 0,
            storDekoratifLamaCost: Number(data.storDekoratifLamaCost) || 0,
            storDekoratifKasaPrice: Number(data.storDekoratifKasaPrice) || 0,
            storDekoratifKasaWholesale: Number(data.storDekoratifKasaWholesale) || 0,
            storDekoratifKasaCost: Number(data.storDekoratifKasaCost) || 0,
            storAltEtekBeyazIsPrice: Number(data.storAltEtekBeyazIsPrice) || 0,
            storAltEtekBeyazIsWholesale: Number(data.storAltEtekBeyazIsWholesale) || 0,
            storAltEtekBeyazIsCost: Number(data.storAltEtekBeyazIsCost) || 0,
            zebraJumboPrice: Number(data.zebraJumboPrice) || 0,
            zebraJumboWholesale: Number(data.zebraJumboWholesale) || 0,
            zebraJumboCost: Number(data.zebraJumboCost) || 0,
            zebraAltEtekDilimPrice: Number(data.zebraAltEtekDilimPrice) || 0,
            zebraAltEtekDilimWholesale: Number(data.zebraAltEtekDilimWholesale) || 0,
            zebraAltEtekDilimCost: Number(data.zebraAltEtekDilimCost) || 0,
            zebraKapaliKasaPrice: Number(data.zebraKapaliKasaPrice) || 0,
            zebraKapaliKasaWholesale: Number(data.zebraKapaliKasaWholesale) || 0,
            zebraKapaliKasaCost: Number(data.zebraKapaliKasaCost) || 0,
            dikeyFonPrice: Number(data.dikeyFonPrice) || 0,
            dikeyFonWholesale: Number(data.dikeyFonWholesale) || 0,
            dikeyFonCost: Number(data.dikeyFonCost) || 0,
            dikeyFonList: data.dikeyFonList || [],
            katlamaliMekanizmaList: data.katlamaliMekanizmaList || [],
            rustikAyakModelList: data.rustikAyakModelList || [],
            rustikAyakCapList: data.rustikAyakCapList || [],
            rustikAyakUzunlukList: data.rustikAyakUzunlukList || [],
            rustikAyakRenkKoduList: data.rustikAyakRenkKoduList || [],
            // Motor Settings Mapping
            storMotorSettings: {
              cost: Number(data.storMotorCost) || 0,
              retail: Number(data.storMotorRetail) || 0,
              wholesale: Number(data.storMotorWholesale) || 0,
              currency: data.storMotorCurrency || 'USD',
              specialRateRetail: Number(data.storMotorSpecialRateRetail) || 0,
              specialRateWholesale: Number(data.storMotorSpecialRateWholesale) || 0
            },
            zebraMotorSettings: {
              cost: Number(data.zebraMotorCost) || 0,
              retail: Number(data.zebraMotorRetail) || 0,
              wholesale: Number(data.zebraMotorWholesale) || 0,
              currency: data.zebraMotorCurrency || 'USD',
              specialRateRetail: Number(data.zebraMotorSpecialRateRetail) || 0,
              specialRateWholesale: Number(data.zebraMotorSpecialRateWholesale) || 0
            },
            ahsapJaluziMotorSettings: {
              cost: Number(data.ahsapJaluziMotorCost) || 0,
              retail: Number(data.ahsapJaluziMotorRetail) || 0,
              wholesale: Number(data.ahsapJaluziMotorWholesale) || 0,
              currency: data.ahsapJaluziMotorCurrency || 'USD',
              specialRateRetail: Number(data.ahsapJaluziMotorSpecialRateRetail) || 0,
              specialRateWholesale: Number(data.ahsapJaluziMotorSpecialRateWholesale) || 0
            },
            metalJaluziMotorSettings: {
              cost: Number(data.metalJaluziMotorCost) || 0,
              retail: Number(data.metalJaluziMotorRetail) || 0,
              wholesale: Number(data.metalJaluziMotorWholesale) || 0,
              currency: data.metalJaluziMotorCurrency || 'USD',
              specialRateRetail: Number(data.metalJaluziMotorSpecialRateRetail) || 0,
              specialRateWholesale: Number(data.metalJaluziMotorSpecialRateWholesale) || 0
            },
            dikeyPerdeMotorSettings: {
              cost: Number(data.dikeyPerdeMotorCost) || 0,
              retail: Number(data.dikeyPerdeMotorRetail) || 0,
              wholesale: Number(data.dikeyPerdeMotorWholesale) || 0,
              currency: data.dikeyPerdeMotorCurrency || 'USD',
              specialRateRetail: Number(data.dikeyPerdeMotorSpecialRateRetail) || 0,
              specialRateWholesale: Number(data.dikeyPerdeMotorSpecialRateWholesale) || 0
            },
            dikeyPvcMotorSettings: {
              cost: Number(data.dikeyPvcMotorCost) || 0,
              retail: Number(data.dikeyPvcMotorRetail) || 0,
              wholesale: Number(data.dikeyPvcMotorWholesale) || 0,
              currency: data.dikeyPvcMotorCurrency || 'USD',
              specialRateRetail: Number(data.dikeyPvcMotorSpecialRateRetail) || 0,
              specialRateWholesale: Number(data.dikeyPvcMotorSpecialRateWholesale) || 0
            },
            dikeyStorMotorSettings: {
              cost: Number(data.dikeyStorMotorCost) || 0,
              retail: Number(data.dikeyStorMotorRetail) || 0,
              wholesale: Number(data.dikeyStorMotorWholesale) || 0,
              currency: data.dikeyStorMotorCurrency || 'USD',
              specialRateRetail: Number(data.dikeyStorMotorSpecialRateRetail) || 0,
              specialRateWholesale: Number(data.dikeyStorMotorSpecialRateWholesale) || 0
            },
            katlamaliPerdeMotorSettings: {
              cost: Number(data.katlamaliPerdeMotorCost) || 0,
              retail: Number(data.katlamaliPerdeMotorRetail) || 0,
              wholesale: Number(data.katlamaliPerdeMotorWholesale) || 0,
              currency: data.katlamaliPerdeMotorCurrency || 'USD',
              specialRateRetail: Number(data.katlamaliPerdeMotorSpecialRateRetail) || 0,
              specialRateWholesale: Number(data.katlamaliPerdeMotorSpecialRateWholesale) || 0
            }
          });
        }
      } catch (err) { }
    };

    fetchSettings();
  }, []);

  // Plise seçilince otomatik bir boş satır ekle (eğer liste boşsa)
  useEffect(() => {
    if (selectedProductType === 'plise' && (!newOrder.pliseItems || newOrder.pliseItems.length === 0)) {
      onFieldChange('pliseItems', [{ id: Date.now(), width: '', height: '', usedM2: '1.00', quantity: '1', multisistem: false, rowTotal: '0.00' }]);
    }
  }, [selectedProductType, newOrder.pliseItems, onFieldChange]);

  // Motor Döviz Hesaplaması (Jaluzi, Stor, Zebra vb.)
  useEffect(() => {
    const motorEnabledTypes = ['ahsapJaluzi', 'metalJaluzi', 'stor', 'zebra', 'dikeyPerde', 'dikeyPvc', 'dikeyStor', 'katlamaliPerde'];
    if (motorEnabledTypes.includes(selectedProductType) && newOrder.motorPriceCurrency !== undefined) {
      const typeSettings = adminSettings[`${selectedProductType}MotorSettings`];
      const currency = typeSettings?.currency || 'USD';
      let rate = exchangeRates[currency] || 1;

      // Özel kur kontrolü
      if (isWholesale) {
        if (typeSettings?.specialRateWholesale && typeSettings.specialRateWholesale !== 0) {
          rate = typeSettings.specialRateWholesale;
        }
      } else {
        if (typeSettings?.specialRateRetail && typeSettings.specialRateRetail !== 0) {
          rate = typeSettings.specialRateRetail;
        }
      }

      const motorTL = (Number(newOrder.motorPriceCurrency) * Number(rate)).toFixed(2);
      if (newOrder.motorPrice !== motorTL) {
        onFieldChange('motorPrice', motorTL);
      }
    }
  }, [newOrder.motorPriceCurrency, adminSettings, exchangeRates, selectedProductType, isWholesale, onFieldChange]);

  // Motor seçildiğinde fiyatı otomatik getir
  useEffect(() => {
    const motorEnabledTypes = ['ahsapJaluzi', 'metalJaluzi', 'stor', 'zebra', 'dikeyPerde', 'dikeyPvc', 'dikeyStor', 'katlamaliPerde'];
    if (motorEnabledTypes.includes(selectedProductType) && newOrder.motorType) {
      const typeSettings = adminSettings[`${selectedProductType}MotorSettings`];
      if (typeSettings) {
        const defaultPrice = isWholesale ? (typeSettings.wholesale || typeSettings.retail) : typeSettings.retail;
        if (newOrder.motorPriceCurrency === '' || newOrder.motorPriceCurrency === undefined || newOrder.motorPriceCurrency === 0) {
          if (newOrder.motorPriceCurrency !== defaultPrice) {
            onFieldChange('motorPriceCurrency', defaultPrice);
          }
        }
      }
    }
  }, [newOrder.motorType, newOrder.motorPriceCurrency, selectedProductType, adminSettings, isWholesale, onFieldChange]);

  // Yardımcı fonksiyon: Bayi ise toptan, değilse perakende fiyatını (veya fallback) döndürür
  const getSettingPrice = (baseKey) => {
    if (isWholesale) {
      const wholesaleVal = adminSettings[`${baseKey}Wholesale`];
      return wholesaleVal !== undefined && wholesaleVal !== null && wholesaleVal !== 0 ? wholesaleVal : adminSettings[`${baseKey}Price`];
    }
    return adminSettings[`${baseKey}Price`];
  };

  // Yardımcı fonksiyon: Ek ayarların maliyetini döndürür
  const getSettingCost = (baseKey) => {
    return Number(adminSettings[`${baseKey}Cost`]) || 0;
  };

  // Dinamik Motor Para Birimi Getir
  const getMotorCurrency = () => {
    const settingsKey = `${selectedProductType}MotorSettings`;
    return adminSettings[settingsKey]?.currency || adminSettings.motorCurrency || 'USD';
  };

  // Firmaları Firestore'dan çek
  useEffect(() => {
    const fetchCompanies = async () => {
      if (!user?.uid) {
        setCompanies([]);
        return;
      }

      try {
        // Artık userId bazlı filtreleme yok, tüm firmaları çek
        const companiesSnapshot = await getDocs(collection(db, 'companies'));
        const companiesList = companiesSnapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name,
          phone: doc.data().phone,
          defaultMessage: doc.data().defaultMessage || "Yeni sipariş bilgileri:"
        }));
        // İsme göre sırala
        companiesList.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setCompanies(companiesList);
      } catch (error) {
        console.error('Firmalar yüklenirken hata:', error);
        setCompanies([]);
      }
    };

    fetchCompanies();
  }, [user]);

  // Pile modellerini sadece constants'tan al (Firestore'dan çekme)
  useEffect(() => {
    // Sadece constants'taki modelleri kullan
    const constantsModels = Object.entries(
      selectedProductType === 'tul' ? TUL_PILE_MODELS : KANAT_PILE_MODELS
    ).map(([key, modelName]) => ({
      id: `const_${key} `,
      modelName: modelName,
      displayName: (selectedProductType === 'tul' ? localizedTulPileModels : localizedKanatPileModels)[key] || modelName,
      density: calculatePileDensity(modelName)
    }));
    setLocalPileModels(constantsModels);
  }, [selectedProductType, localizedTulPileModels, localizedKanatPileModels]);

  // Ürün Listesi'nden ürünleri çek (ürün tipine göre filtrele)
  const fetchProductItems = useCallback(async (productType) => {
    if (!productType) {
      setProductItems([]);
      return;
    }
    try {
      const snapshot = await getDocs(collection(db, 'productItems'));
      const all = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      const filtered = all
        .filter(p => p.productType === productType)
        .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr'));
      setProductItems(filtered);
    } catch (error) {
      console.error('Ürün listesi yüklenirken hata:', error);
      setProductItems([]);
    }
  }, []);

  useEffect(() => {
    fetchProductItems(selectedProductType);
  }, [selectedProductType, fetchProductItems]);

  // Ürün seçilince adı ve birim fiyatı otomatik doldur
  const handleProductSelect = (productId) => {
    const rawProduct = productItems.find(p => p.id === productId);
    const product = rawProduct ? convertProductToKztForOrder(rawProduct, exchangeRates) : null;
    if (product) {
      onFieldChange('productName', product.name);

      // isWholesale modunda toptan fiyatı kullan (yoksa birim fiyata fallback)
      // Eğer bir Jaluzi seçiliyse, unitPrice sistem tipine veya varsa ilk geçerli fiyata göre seçilmeli.
      let priceToSet;
      if (selectedProductType === 'ahsapJaluzi') {
        priceToSet = resolveJaluziOrderPricing(product, newOrder.sistemTipi, isWholesale).price;
      } else if (isWholesale) {
        // Diğer ürün tipleri: wholesalePrice (yoksa unitPrice fallback)
        priceToSet = (product.wholesalePrice && product.wholesalePrice > 0)
          ? product.wholesalePrice
          : product.unitPrice;
      } else {
        priceToSet = product.unitPrice;
      }

      let extra = 0;
      let extraCost = 0;
      if (selectedProductType === 'tul' || selectedProductType === 'kanat') {
        if (newOrder.pileModel === 'S Pile') {
          extra = getSettingPrice('sPile');
          extraCost = getSettingCost('sPile');
        }
        if (newOrder.pileModel === "Amerikan Pile 2'li") {
          extra = getSettingPrice('american2');
          extraCost = getSettingCost('american2');
        }
        if (newOrder.pileModel === "Amerikan Pile 3'lü") {
          extra = getSettingPrice('american3');
          extraCost = getSettingCost('american3');
        }
        if (newOrder.pileModel === 'Kapsül Pile') {
          const capUnitPrice = getSettingPrice('kapsul');
          onFieldChange('capsuleUnitPrice', capUnitPrice);
          extraCost = getSettingCost('kapsul');
        }
      } else if (selectedProductType === 'ahsapJaluzi') {
        // Ahşap Jaluzi için ek ücretler (varsa) buraya eklenebilir
      } else if (selectedProductType === 'metalJaluzi') {
        if (newOrder.hasMetalReduktor) {
          extra += getSettingPrice('metalReduktor');
          extraCost += getSettingCost('metalReduktor');
        }
      } else if (selectedProductType === 'dikeyPerde') {
        if (newOrder.hasDikeyFon) {
          extra += getSettingPrice('dikeyFon');
          extraCost += getSettingCost('dikeyFon');
        }
      }

      if (selectedProductType === 'katlamaliPerde') {
        onFieldChange('fabricPrice', priceToSet);
      }

      // Maliyet hesaplama
      let costToSet = product.cost || 0;
      if (selectedProductType === 'ahsapJaluzi') {
        costToSet = resolveJaluziOrderPricing(product, newOrder.sistemTipi, isWholesale).cost;
      }
      onFieldChange('__batch__', {
        productName: product.name,
        productItemId: product.id,
        fabricCost: costToSet,
        baseUnitCost: costToSet,
        unitCost: Number(costToSet) + extraCost,
        unitPrice: Number(priceToSet) + extra,
        baseUnitPrice: priceToSet
      });
    } else {
      onFieldChange('__batch__', {
        productName: '',
        productItemId: '',
        unitPrice: '',
        baseUnitPrice: '',
        fabricCost: '',
        baseUnitCost: '',
        unitCost: ''
      });
    }
  };

  const handlePliseProduct1Select = (productId) => {
    const rawProduct = productItems.find(p => p.id === productId);
    const product = rawProduct ? convertProductToKztForOrder(rawProduct, exchangeRates) : null;
    if (product) {
      onFieldChange('pliseProduct1Name', product.name);
      const priceToSet = isWholesale
        ? (product.wholesalePrice && product.wholesalePrice > 0 ? product.wholesalePrice : product.unitPrice || '')
        : (product.unitPrice || '');
      onFieldChange('pliseProduct1Price', priceToSet);
      onFieldChange('pliseProduct1Cost', product.cost || 0);
    } else {
      onFieldChange('pliseProduct1Name', '');
      onFieldChange('pliseProduct1Price', '');
      onFieldChange('pliseProduct1Cost', 0);
    }
  };

  const handlePliseProduct2Select = (productId) => {
    const rawProduct = productItems.find(p => p.id === productId);
    const product = rawProduct ? convertProductToKztForOrder(rawProduct, exchangeRates) : null;
    if (product) {
      onFieldChange('pliseProduct2Name', product.name);
      const priceToSet = isWholesale
        ? (product.wholesalePrice && product.wholesalePrice > 0 ? product.wholesalePrice : product.unitPrice || '')
        : (product.unitPrice || '');
      onFieldChange('pliseProduct2Price', priceToSet);
      onFieldChange('pliseProduct2Cost', product.cost || 0);
    } else {
      onFieldChange('pliseProduct2Name', '');
      onFieldChange('pliseProduct2Price', '');
      onFieldChange('pliseProduct2Cost', 0);
    }
  };

  const handleKatlamaliMechanismSelect = (mechanismType) => {
    const rawMechanism = adminSettings.katlamaliMekanizmaList.find(m => m.type === mechanismType);
    const mechanism = rawMechanism ? convertMechanismToKztForOrder(rawMechanism, exchangeRates) : null;
    if (mechanism) {
      const wholesalePrice = (mechanism.wholesale !== undefined && mechanism.wholesale !== null && mechanism.wholesale !== 0) ? mechanism.wholesale : mechanism.price;
      const finalPrice = isWholesale ? wholesalePrice : mechanism.price;

      onFieldChange('mechanismType', mechanism.type);
      onFieldChange('mechanismPrice', finalPrice);

      // UnitCost hesabına mekanizma maliyetini ekle
      const baseCost = Number(newOrder.baseUnitCost) || 0;
      const mekCost = Number(mechanism.cost) || 0;
      onFieldChange('unitCost', baseCost + mekCost);
    } else {
      onFieldChange('mechanismType', mechanismType);
      onFieldChange('mechanismPrice', '');

      const baseCost = Number(newOrder.baseUnitCost) || 0;
      onFieldChange('unitCost', baseCost);
    }
  };

  const handleAddAccessory = () => {
    const currentAccessories = Array.isArray(newOrder.accessories) ? newOrder.accessories : [];
    const newAccessories = [...currentAccessories, { id: Date.now().toString(), name: '', price: '', quantity: '1', total: '0' }];
    onFieldChange('accessories', newAccessories);
  };

  const handleRemoveAccessory = (id) => {
    const currentAccessories = Array.isArray(newOrder.accessories) ? newOrder.accessories : [];
    const newAccessories = currentAccessories.filter(acc => acc.id !== id);
    onFieldChange('accessories', newAccessories);
  };

  const handleAccessoryChange = (id, field, value) => {
    const currentAccessories = Array.isArray(newOrder.accessories) ? newOrder.accessories : [];
    const newAccessories = currentAccessories.map(acc => {
      if (acc.id === id) {
        const updatedAcc = { ...acc, [field]: value };
        if (field === 'price' || field === 'quantity') {
          const p = parseFloat(updatedAcc.price) || 0;
          const q = parseFloat(updatedAcc.quantity) || 0;
          updatedAcc.total = (p * q).toFixed(2);
        }
        return updatedAcc;
      }
      return acc;
    });
    onFieldChange('accessories', newAccessories);
  };

  const handleAstarProductSelect = (productId) => {
    const rawProduct = productItems.find((p) => p.id === productId);
    const product = rawProduct ? convertProductToKztForOrder(rawProduct, exchangeRates) : null;
    if (product) {
      onFieldChange('__batch__', {
        astarProductName: product.name,
        astarProductItemId: product.id,
        astarUnitPrice: product.unitPrice || '',
        astarUnitCost: product.cost || 0
      });
    }
  };

  const handleKanatAstarToggle = () => {
    const nextOpen = !newOrder.hasKanatAstar;
    if (nextOpen) {
      onFieldChange('hasKanatAstar', true);
      return;
    }
    onFieldChange('__batch__', {
      hasKanatAstar: false,
      astarProductName: '',
      astarProductItemId: '',
      astarProductVariant: '',
      astarUnitPrice: '',
      astarUnitCost: '',
      astarFee: '',
      astarCost: ''
    });
  };

  // Ürün için unique key oluştur
  const createProductKey = useCallback(() => {
    const { roomName, productName, productType } = newOrder;
    if (!roomName || !productName || !productType) return 'temp';

    // Oda + Ürün Adı + Ürün Tipi kombinasyonu ile unique key
    return `${productType} -${roomName} -${productName} `.replace(/[^a-zA-Z0-9-]/g, '').toLowerCase();
  }, [newOrder]);

  // Component mount olduğunda ve ürün bilgileri değiştiğinde localStorage'dan verileri yükle
  useEffect(() => {
    if (customerId && !customerId.startsWith('temp_')) {
      // Ürün bazında unique key oluştur
      const productKey = createProductKey();
      if (productKey !== 'temp') {
        const savedData = localStorage.getItem(`whatsapp - ${customerId} -${productKey} `);
        if (savedData) {
          try {
            const { selectedFirmaId, lastSentFirmaInfo, messageLang } = JSON.parse(savedData);
            setSelectedFirma(selectedFirmaId || '');
            setLastSentFirma(lastSentFirmaInfo || null);
            setWhatsappMessageLang(messageLang === 'en' ? 'en' : 'tr');
          } catch (error) {
            console.error('WhatsApp verileri yüklenirken hata:', error);
          }
        } else {
          // Bu ürün için kayıtlı veri yoksa sıfırla
          setSelectedFirma('');
          setLastSentFirma(null);
        }
      }
    }
  }, [customerId, createProductKey]);

  // WhatsApp gönderme fonksiyonu
  const handleWhatsAppSend = () => {
    if (!selectedFirma) {
      alert(of('alerts.selectCompany'));
      return;
    }

    if (!customerInfo || !newOrder || !selectedProductType) {
      alert(of('alerts.customerNotFound'));
      return;
    }

    // Firestore'dan gelen firmaları kullan, yoksa constants'taki listeyi kullan
    const selectedFirmaInfo = companies.find(firma => firma.id === selectedFirma);
    if (selectedFirmaInfo) {
      // newOrder'a productType ekleyerek gönder
      const orderWithType = { ...newOrder, productType: selectedProductType };
      sendSingleOrderWhatsAppMessage(customerInfo, orderWithType, selectedFirmaInfo, null, whatsappMessageLang);

      // Son gönderilen firmayı kaydet
      setLastSentFirma(selectedFirmaInfo);

      // localStorage'a ürün bazında kaydet
      if (customerId && !customerId.startsWith('temp_')) {
        const productKey = createProductKey();
        const dataToSave = {
          selectedFirmaId: selectedFirma,
          lastSentFirmaInfo: selectedFirmaInfo,
          messageLang: whatsappMessageLang
        };
        localStorage.setItem(`whatsapp - ${customerId} -${productKey} `, JSON.stringify(dataToSave));
      }
    }
  };

  // Katlamalı Perde için çoklu WhatsApp gönderme fonksiyonu
  const handleKatlamaliWhatsAppSend = (subType) => {
    let firmaId = '';
    if (subType === 'kumas') firmaId = selectedFirmaKumas;
    else if (subType === 'mekanizma') firmaId = selectedFirmaMekanizma;
    else if (subType === 'motor') firmaId = selectedFirmaMotor;
    else if (subType === 'aksesuar') firmaId = selectedFirmaAksesuar;

    if (!firmaId) {
      alert(of('alerts.selectCompany'));
      return;
    }

    if (!customerInfo || !newOrder || !selectedProductType) {
      alert(of('alerts.customerNotFound'));
      return;
    }

    const selectedFirmaInfo = companies.find(firma => firma.id === firmaId);
    if (selectedFirmaInfo) {
      const orderWithType = { ...newOrder, productType: selectedProductType };
      sendSingleOrderWhatsAppMessage(customerInfo, orderWithType, selectedFirmaInfo, subType, whatsappMessageLang);

      // Son gönderilene kaydet
      if (subType === 'kumas') setLastSentFirmaKumas(selectedFirmaInfo);
      else if (subType === 'mekanizma') setLastSentFirmaMekanizma(selectedFirmaInfo);
      else if (subType === 'motor') setLastSentFirmaMotor(selectedFirmaInfo);
      else if (subType === 'aksesuar') setLastSentFirmaAksesuar(selectedFirmaInfo);
    }
  };

  const renderWhatsappLanguageSelect = (visible = false) => (
    visible ? (
      <div className="whatsapp-lang-select whatsapp-lang-select-inline">
        <span className="whatsapp-lang-label">{of('whatsapp.messageLanguage')}</span>
        <div className="whatsapp-lang-options">
          <button
            type="button"
            className={`whatsapp-lang-btn ${whatsappMessageLang === 'tr' ? 'active' : ''}`}
            onClick={() => setWhatsappMessageLang('tr')}
            disabled={isLoading}
          >
            {of('whatsapp.languageTr')}
          </button>
          <button
            type="button"
            className={`whatsapp-lang-btn ${whatsappMessageLang === 'en' ? 'active' : ''}`}
            onClick={() => setWhatsappMessageLang('en')}
            disabled={isLoading}
          >
            {of('whatsapp.languageEn')}
          </button>
        </div>
      </div>
    ) : null
  );

  // react-select için stil özelleştirmesi
  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      padding: '0px 4px',
      minHeight: '42px',
      border: state.isFocused ? '1px solid #b04309' : '1px solid #ddd',
      boxShadow: 'none',
      borderRadius: '6px',
      '&:hover': {
        border: state.isFocused ? '1px solid #b04309' : '1px solid #cbd5e1'
      }
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '0 8px',
      alignItems: 'center',
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
    }),
    input: (provided) => ({
      ...provided,
      margin: '0px',
      padding: '0px',
      'input:focus': {
        boxShadow: 'none !important'
      }
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#b04309' : state.isFocused ? '#fff5f0' : 'white',
      color: state.isSelected ? 'white' : '#333',
      cursor: 'pointer',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#333',
    })
  };

  const portalSelectStyles = {
    ...customSelectStyles,
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999
    })
  };

  const whatsappSelectStyles = {
    ...customSelectStyles,
    control: (provided, state) => ({
      ...customSelectStyles.control(provided, state),
      minHeight: '42px',
      height: '42px',
      alignItems: 'center',
      overflow: 'hidden',
    }),
    valueContainer: (provided, state) => ({
      ...customSelectStyles.valueContainer(provided, state),
      height: '40px',
      padding: '0 8px',
      alignItems: 'center',
      position: 'relative',
    }),
    indicatorsContainer: (provided, state) => ({
      ...customSelectStyles.indicatorsContainer(provided, state),
      height: '40px',
      alignItems: 'center',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#333',
      margin: 0,
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      maxWidth: 'calc(100% - 4px)',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 20,
      minWidth: '100%',
      width: 'max-content',
      maxWidth: '320px',
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: '220px',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#b04309' : state.isFocused ? '#fff5f0' : 'white',
      color: state.isSelected ? 'white' : '#333',
      cursor: 'pointer',
      whiteSpace: 'normal',
      wordBreak: 'break-word',
    }),
    placeholder: (provided) => ({
      ...provided,
      margin: 0,
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
    }),
    input: (provided) => ({
      ...provided,
      margin: '0',
      padding: '0',
      'input:focus': {
        boxShadow: 'none !important'
      }
    }),
  };

  // Ürün dropdown için ortak options objesi oluşturma
  const productOptions = productItems.map((p) => ({ value: p.id, label: p.name }));

  return (
    <div className="order-form">
      <h5>{editingOrderId ? of('editTitle') : of('addTitle')}</h5>
      <form onSubmit={onSubmit}>
        {/* Ürün Tipi Seçimi */}
        <div className="form-group">
          <label>{of('labels.productType')}</label>
          <select
            value={selectedProductType}
            onChange={(e) => onProductTypeChange(e.target.value)}
            required
            disabled={isLoading}
          >
            <option value="">{of('placeholders.selectProductType')}</option>
            {Object.entries(localizedProductTypes).map(([key, product]) => (
              <option key={key} value={key}>{product.label}</option>
            ))}
          </select>
        </div>

        {selectedProductType && (
          <div className="dynamic-fields">
            {/* Tül ve Kanat için özel alanlar */}
            {(selectedProductType === 'tul' || selectedProductType === 'kanat') && (
              <>
                {!isWholesale && (
                <div className="form-group">
                  <label>{of('labels.roomName')}</label>
                  <input
                    type="text"
                    value={newOrder.roomName}
                    onChange={(e) => onFieldChange('roomName', e.target.value)}
                    disabled={isLoading}
                  />
                </div>
                )}

                <div className="form-group">
                  <label>{of('labels.productName')}</label>
                  <CreatableSelect
                    options={productOptions}
                    value={
                      productOptions.find(o => o.value === (productItems.find(p => p.name === newOrder.productName)?.id)) ||
                      (newOrder.productName ? { value: newOrder.productName, label: newOrder.productName } : null)
                    }
                    onChange={(option) => {
                      if (!option) {
                        onFieldChange('__batch__', {
                          productName: '',
                          productItemId: '',
                          unitPrice: '',
                          baseUnitPrice: '',
                          fabricCost: '',
                          baseUnitCost: '',
                          unitCost: ''
                        });
                      } else if (option.__isNew__) {
                        onFieldChange('__batch__', {
                          productName: option.value,
                          unitPrice: '',
                          baseUnitPrice: '',
                          fabricCost: '',
                          baseUnitCost: '',
                          unitCost: ''
                        });
                      } else {
                        handleProductSelect(option.value);
                      }
                    }}
                    isDisabled={isLoading}
                    isClearable
                    isSearchable
                    placeholder={of('placeholders.selectProduct')}
                    noOptionsMessage={() => of('messages.noProduct')}
                    formatCreateLabel={(inputValue) => of('placeholders.createFormat', { value: inputValue })}
                    styles={customSelectStyles}
                  />
                </div>

                <div className="form-group">
                  <label>{of('labels.productVariant')}</label>
                  <input
                    type="text"
                    value={newOrder.productVariant || ''}
                    onChange={(e) => onFieldChange('productVariant', e.target.value)}
                    disabled={isLoading}
                    placeholder={of('placeholders.productVariant')}
                  />
                </div>

                <div className="form-group">
                  <label>{isWholesale ? of('labels.widthM') : of('labels.widthCm')}</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newOrder.width}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('width', e.target.value)}
                    required
                    disabled={isLoading}
                    placeholder={isWholesale ? 'Metre cinsinden giriniz' : ''}
                  />
                </div>

                {!isWholesale && (
                <div className="form-group">
                  <label>{of('labels.heightCm')}</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newOrder.height}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('height', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>
                )}

                <div className="form-group">
                  <div className="label-with-icon">
                    <label>{of('labels.unitPrice')}</label>
                    <button
                      type="button"
                      className={`lock-btn ${showBaseCost ? 'active' : ''}`}
                      onClick={() => setShowBaseCost(!showBaseCost)}
                      title={of('titles.costInfo')}
                    >
                      <i className={`fas ${showBaseCost ? 'fa-lock-open' : 'fa-lock'}`}></i>
                    </button>
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={newOrder.unitPrice}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => {
                      onFieldChange('unitPrice', e.target.value);
                      onFieldChange('baseUnitPrice', e.target.value);
                    }}
                    required
                    disabled={isLoading}
                  />
                </div>

                {showBaseCost && (
                  <div className="form-group">
                    <label className="extra-cost-label">{of('labels.productCost')}</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newOrder.baseUnitCost || ''}
                      onKeyDown={handleNumericKeyDown}
                      onChange={(e) => onFieldChange('baseUnitCost', e.target.value)}
                      disabled={isLoading}
                      placeholder={of('placeholders.hiddenCost')}
                    />
                  </div>
                )}

                {!isWholesale && (
                <>
                <div className="form-group">
                  <label>{of('labels.pileModel')}</label>
                  <select
                    value={newOrder.pileModel || ''}
                    onChange={(e) => {
                      const selectedModel = e.target.value;
                      onFieldChange('pileModel', selectedModel);

                      if ((selectedProductType === 'tul' || selectedProductType === 'kanat') && newOrder.baseUnitPrice !== undefined) {
                        let extra = 0;
                        let extraCost = 0;
                        if (selectedModel === 'S Pile') {
                          extra = getSettingPrice('sPile');
                          extraCost = getSettingCost('sPile');
                        }
                        if (selectedModel === "Amerikan Pile 2'li") {
                          extra = getSettingPrice('american2');
                          extraCost = getSettingCost('american2');
                        }
                        if (selectedModel === "Amerikan Pile 3'lü") {
                          extra = getSettingPrice('american3');
                          extraCost = getSettingCost('american3');
                        }
                        if (selectedModel === 'Kapsül Pile') {
                          const capUnitPrice = getSettingPrice('kapsul');
                          onFieldChange('capsuleUnitPrice', capUnitPrice);
                          extraCost = getSettingCost('kapsul');
                        }
                        onFieldChange('unitPrice', (Number(newOrder.baseUnitPrice) || 0) + extra);
                        onFieldChange('unitCost', (Number(newOrder.baseUnitCost) || 0) + extraCost);
                      }
                    }}
                    required
                    disabled={isLoading}
                  >
                    <option value="">{of('placeholders.selectPileModel')}</option>
                    {localPileModels.map((model) => (
                      <option key={model.id} value={model.modelName}>
                        {model.displayName}
                      </option>
                    ))}
                  </select>
                </div>

                {newOrder.pileModel && (
                  <div className="form-group">
                    <label>{of('labels.pileDensity')}</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0.1"
                      value={newOrder.pileDensity || ''}
                      onChange={(e) => onFieldChange('pileDensity', e.target.value)}
                      disabled={isLoading}
                      placeholder={of('placeholders.pileDensity')}
                    />
                    <small>{of('titles.pileDensityEditable')}</small>
                  </div>
                )}

                {newOrder.usedM2 && (
                  <div className="form-group">
                    <label>{of('labels.usedMt')}</label>
                    <input
                      type="text"
                      value={newOrder.usedM2}
                      readOnly
                      className="readonly-field"
                    />
                  </div>
                )}

                <div className="form-group">
                  <div className="label-with-icon">
                    <label>{of('labels.extraFee')}</label>
                    <button
                      type="button"
                      className={`lock-btn ${showExtraCost ? 'active' : ''}`}
                      onClick={() => setShowExtraCost(!showExtraCost)}
                      title={of('titles.extraCostEntry')}
                    >
                      <i className={`fas ${showExtraCost ? 'fa-lock-open' : 'fa-lock'}`}></i>
                    </button>
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newOrder.extraPrice}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => {
                      onFieldChange('extraPrice', e.target.value);
                      onFieldChange('manualExtraCost', '');
                    }}
                    disabled={isLoading}
                  />
                </div>

                {showExtraCost && (
                  <div className="form-group">
                    <label className="extra-cost-label">{of('labels.extraFeeCost')}</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newOrder.manualExtraCost || ''}
                      onKeyDown={handleNumericKeyDown}
                      onChange={(e) => onFieldChange('manualExtraCost', e.target.value)}
                      disabled={isLoading}
                      placeholder={of('placeholders.hiddenCost')}
                    />
                  </div>
                )}

                {newOrder.pileModel === 'Kapsül Pile' && (
                  <>
                    <div className="form-group">
                      <label>{of('labels.capsuleUnitPrice')}</label>
                      <input
                        type="number"
                        step="0.01"
                        value={newOrder.capsuleUnitPrice}
                        onChange={(e) => onFieldChange('capsuleUnitPrice', e.target.value)}
                        required
                        disabled={isLoading}
                      />
                    </div>

                    {newOrder.capsulePrice && (
                      <div className="form-group">
                        <label>{of('labels.capsuleFee')}</label>
                        <input
                          type="text"
                          value={formatPrice(newOrder.capsulePrice)}
                          readOnly
                          className="readonly-field"
                        />
                      </div>
                    )}
                  </>
                )}
                </>
                )}

                <div className="form-group">
                  <label>{of('labels.quantityRequired')}</label>
                  <input
                    type="number"
                    step="1"
                    min="1"
                    value={newOrder.quantity || ''}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('quantity', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                {selectedProductType === 'kanat' && !isWholesale && (
                  <div className={`kanat-astar-section${newOrder.hasKanatAstar ? ' is-open' : ''}`}>
                    <button
                      type="button"
                      className="kanat-astar-toggle"
                      onClick={handleKanatAstarToggle}
                      disabled={isLoading}
                    >
                      <i className={`fas fa-chevron-${newOrder.hasKanatAstar ? 'up' : 'down'}`} />
                      <i className="fas fa-layer-group" style={{ marginRight: '4px' }} />
                      {of('sections.kanatAstar')}
                    </button>

                    {newOrder.hasKanatAstar && (
                      <div className="kanat-astar-body">
                        <div className="form-group">
                          <label>{of('labels.astarProductName')}</label>
                          <CreatableSelect
                            options={productOptions}
                            value={
                              productOptions.find((o) => o.value === (productItems.find((p) => p.name === newOrder.astarProductName)?.id)) ||
                              (newOrder.astarProductName ? { value: newOrder.astarProductName, label: newOrder.astarProductName } : null)
                            }
                            onChange={(option) => {
                              if (!option) {
                                onFieldChange('__batch__', {
                                  astarProductName: '',
                                  astarProductItemId: '',
                                  astarUnitPrice: '',
                                  astarUnitCost: ''
                                });
                              } else if (option.__isNew__) {
                                onFieldChange('__batch__', {
                                  astarProductName: option.value,
                                  astarProductItemId: '',
                                  astarUnitPrice: '',
                                  astarUnitCost: ''
                                });
                              } else {
                                handleAstarProductSelect(option.value);
                              }
                            }}
                            isDisabled={isLoading}
                            isClearable
                            isSearchable
                            placeholder={of('placeholders.selectProduct')}
                            noOptionsMessage={() => of('messages.noProduct')}
                            formatCreateLabel={(inputValue) => of('placeholders.createFormat', { value: inputValue })}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={portalSelectStyles}
                          />
                        </div>

                        <div className="form-group">
                          <label>{of('labels.astarProductVariant')}</label>
                          <input
                            type="text"
                            value={newOrder.astarProductVariant || ''}
                            onChange={(e) => onFieldChange('astarProductVariant', e.target.value)}
                            disabled={isLoading}
                            placeholder={of('placeholders.productVariant')}
                          />
                        </div>

                        <div className="form-group">
                          <label>{of('labels.astarUnitPrice')}</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={newOrder.astarUnitPrice || ''}
                            onKeyDown={handleNumericKeyDown}
                            onChange={(e) => onFieldChange('astarUnitPrice', e.target.value)}
                            disabled={isLoading}
                          />
                        </div>

                        <div className="form-group">
                          <label>{of('labels.kanatAstarFee')}</label>
                          <input
                            type="text"
                            value={newOrder.astarFee ? formatPrice(newOrder.astarFee) : '-'}
                            readOnly
                            className="readonly-field"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="form-group">
                  <label>{of('labels.discount')}</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={newOrder.discount}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('discount', e.target.value)}
                    disabled={isLoading}
                  />
                </div>

                {newOrder.totalPrice && (
                  <div className="form-group">
                    <label>{of('labels.totalAmount')}</label>
                    <input
                      type="text"
                      value={formatPrice(newOrder.totalPrice)}
                      readOnly
                      className="total-price"
                    />
                  </div>
                )}

                {newOrder.discountedPrice && parseFloat(newOrder.discount) > 0 && (
                  <div className="form-group">
                    <label>{of('labels.discountedAmount')}</label>
                    <input
                      type="text"
                      value={formatPrice(newOrder.discountedPrice)}
                      readOnly
                      className="discounted-price"
                    />
                  </div>
                )}
              </>
            )}

            {/* Stor ve Zebra için özel alanlar */}
            {(selectedProductType === 'stor' || selectedProductType === 'zebra') && (
              <>
                {!isWholesale && (
                <div className="form-group">
                  <label>{of('labels.roomName')}</label>
                  <input
                    type="text"
                    value={newOrder.roomName}
                    onChange={(e) => onFieldChange('roomName', e.target.value)}
                    disabled={isLoading}
                  />
                </div>
                )}

                <div className="form-group">
                  <label>{of('labels.productName')}</label>
                  <CreatableSelect
                    options={productOptions}
                    value={
                      productOptions.find(o => o.value === (productItems.find(p => p.name === newOrder.productName)?.id)) ||
                      (newOrder.productName ? { value: newOrder.productName, label: newOrder.productName } : null)
                    }
                    onChange={(option) => {
                      if (!option) {
                        onFieldChange('productName', '');
                        onFieldChange('unitPrice', '');
                      } else if (option.__isNew__) {
                        onFieldChange('productName', option.value);
                        onFieldChange('unitPrice', '');
                      } else {
                        handleProductSelect(option.value);
                      }
                    }}
                    isDisabled={isLoading}
                    isClearable
                    isSearchable
                    placeholder={of('placeholders.selectProduct')}
                    noOptionsMessage={() => of('messages.noProduct')}
                    formatCreateLabel={(inputValue) => of('placeholders.createFormat', { value: inputValue })}
                    styles={customSelectStyles}
                  />
                </div>

                <div className="form-group">
                  <label>{of('labels.productVariant')}</label>
                  <input
                    type="text"
                    value={newOrder.productVariant || ''}
                    onChange={(e) => onFieldChange('productVariant', e.target.value)}
                    disabled={isLoading}
                    placeholder={of('placeholders.productVariant')}
                  />
                </div>

                <div className="form-group">
                  <label>{of('labels.widthCm')}</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newOrder.width}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('width', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>{of('labels.heightCm')}</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newOrder.height}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('height', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>{of('labels.direction')}</label>
                  <select
                    value={newOrder.direction || ''}
                    onChange={(e) => onFieldChange('direction', e.target.value)}
                    required
                    disabled={isLoading}
                  >
                    <option value="">{of('placeholders.select')}</option>
                    {Object.entries(DIRECTION_TYPES).map(([key, value]) => (
                      <option key={key} value={value}>{localizedDirectionTypes[key]}</option>
                    ))}
                  </select>
                </div>

                {newOrder.usedM2 && (
                  <div className="form-group">
                    <label>{of('labels.usedMt2')}</label>
                    <input
                      type="text"
                      value={newOrder.usedM2}
                      readOnly
                      className="readonly-field"
                    />
                  </div>
                )}

                <div className="form-group">
                  <div className="label-with-icon">
                    <label>{of('labels.unitPrice')}</label>
                    <button
                      type="button"
                      className={`lock-btn ${showBaseCost ? 'active' : ''}`}
                      onClick={() => setShowBaseCost(!showBaseCost)}
                      title={of('titles.costInfo')}
                    >
                      <i className={`fas ${showBaseCost ? 'fa-lock-open' : 'fa-lock'}`}></i>
                    </button>
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={newOrder.unitPrice}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => {
                      onFieldChange('unitPrice', e.target.value);
                      onFieldChange('baseUnitCost', '');
                    }}
                    required
                    disabled={isLoading}
                  />
                </div>

                {showBaseCost && (
                  <div className="form-group">
                    <label className="extra-cost-label">{of('labels.unitCost')}</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newOrder.baseUnitCost || ''}
                      onKeyDown={handleNumericKeyDown}
                      onChange={(e) => onFieldChange('baseUnitCost', e.target.value)}
                      disabled={isLoading}
                      placeholder={of('placeholders.hiddenCost')}
                    />
                  </div>
                )}


                {(selectedProductType === 'stor' || selectedProductType === 'zebra') && (
                  <>
                    <div className="form-group">
                      <label>{of('labels.motor')}</label>
                      <select
                        value={newOrder.motorType || ''}
                        onChange={(e) => onFieldChange('motorType', e.target.value)}
                        disabled={isLoading}
                      >
                        <option value="">{of('placeholders.select')}</option>
                        {Object.entries(MOTOR_TYPES).map(([key, value]) => (
                          <option key={key} value={value}>{localizedMotorTypes[key]}</option>
                        ))}
                      </select>
                    </div>

                    {newOrder.motorType && (
                      <div className="form-group">
                        <label>{of('labels.motorFee', { currency: getMotorCurrency() })}</label>
                        <input
                          type="number"
                          step="0.01"
                          value={newOrder.motorPriceCurrency || ''}
                          onChange={(e) => onFieldChange('motorPriceCurrency', e.target.value)}
                          disabled={isLoading}
                        />
                        {newOrder.motorPriceCurrency && (
                          <small style={{ display: 'block', marginTop: '5px', color: '#b04309' }}>
                            ≈ {newOrder.motorPrice ? Number(newOrder.motorPrice).toFixed(2) : 0} ₺
                          </small>
                        )}
                      </div>
                    )}
                  </>
                )}

                <div className="form-group">
                  <div className="label-with-icon">
                    <label>{of('labels.mounting')}</label>
                    <button
                      type="button"
                      className={`lock-btn ${showMountingCost ? 'active' : ''}`}
                      onClick={() => setShowMountingCost(!showMountingCost)}
                      title={of('titles.mountingCostEntry')}
                    >
                      <i className={`fas ${showMountingCost ? 'fa-lock-open' : 'fa-lock'}`}></i>
                    </button>
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    value={newOrder.mounting || ''}
                    onChange={(e) => onFieldChange('mounting', e.target.value)}
                    disabled={isLoading}
                  />
                </div>
                {showMountingCost && (
                  <div className="form-group">
                    <label className="extra-cost-label">{of('labels.mountingCost')}</label>
                    <input
                      type="number"
                      step="0.01"
                      value={newOrder.mountingCost || ''}
                      onChange={(e) => onFieldChange('mountingCost', e.target.value)}
                      onKeyDown={handleNumericKeyDown}
                      disabled={isLoading}
                      placeholder={of('placeholders.hiddenCost')}
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>{of('labels.quantityRequired')}</label>
                  <input
                    type="number"
                    step="1"
                    min="1"
                    value={newOrder.quantity || ''}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('quantity', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>{of('labels.discount')}</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={newOrder.discount}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('discount', e.target.value)}
                    disabled={isLoading}
                  />
                </div>

                <div className="order-extras-grid">
                  {selectedProductType === 'stor' && (
                    <>
                      <div className="form-group order-extra-checkbox-item">
                        <label className="order-extra-checkbox-label">
                          <input type="checkbox" checked={newOrder.hasStorJumbo || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasStorJumbo', checked); onFieldChange('storJumboAmount', checked ? getSettingPrice('storJumbo') : 0); }} />
                          <div>
                            <span>{of('extras.jumboSystem')}</span>
                            <small>{getSettingPrice('storJumbo')} ₸</small>
                          </div>
                        </label>
                      </div>
                      <div className="form-group order-extra-checkbox-item">
                        <label className="order-extra-checkbox-label">
                          <input type="checkbox" checked={newOrder.hasStorAltEtekDilim || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasStorAltEtekDilim', checked); onFieldChange('storAltEtekDilimAmount', checked ? getSettingPrice('storAltEtekDilim') : 0); }} />
                          <div>
                            <span>{of('extras.bottomHemSlice')}</span>
                            <small>{getSettingPrice('storAltEtekDilim')} ₸</small>
                          </div>
                        </label>
                      </div>
                      <div className="form-group order-extra-checkbox-item">
                        <label className="order-extra-checkbox-label">
                          <input type="checkbox" checked={newOrder.hasStorKapaliKasa || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasStorKapaliKasa', checked); onFieldChange('storKapaliKasaAmount', checked ? getSettingPrice('storKapaliKasa') : 0); }} />
                          <div>
                            <span>{of('extras.closedCase')}</span>
                            <small>{getSettingPrice('storKapaliKasa')} ₸</small>
                          </div>
                        </label>
                      </div>
                      <div className="form-group order-extra-checkbox-item">
                        <label className="order-extra-checkbox-label">
                          <input type="checkbox" checked={newOrder.hasStorAltEtekLazer || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasStorAltEtekLazer', checked); onFieldChange('storAltEtekLazerAmount', checked ? getSettingPrice('storAltEtekLazer') : 0); }} />
                          <div>
                            <span>{of('extras.bottomHemLaser')}</span>
                            <small>{getSettingPrice('storAltEtekLazer')} ₸</small>
                          </div>
                        </label>
                      </div>
                      <div className="form-group order-extra-checkbox-item">
                        <label className="order-extra-checkbox-label">
                          <input type="checkbox" checked={newOrder.hasStorDekoratifLama || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasStorDekoratifLama', checked); onFieldChange('storDekoratifLamaAmount', checked ? getSettingPrice('storDekoratifLama') : 0); }} />
                          <div>
                            <span>{of('extras.decorativeSlat')}</span>
                            <small>{getSettingPrice('storDekoratifLama')} ₸</small>
                          </div>
                        </label>
                      </div>
                      <div className="form-group order-extra-checkbox-item">
                        <label className="order-extra-checkbox-label">
                          <input type="checkbox" checked={newOrder.hasStorDekoratifKasa || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasStorDekoratifKasa', checked); onFieldChange('storDekoratifKasaAmount', checked ? getSettingPrice('storDekoratifKasa') : 0); }} />
                          <div>
                            <span>{of('extras.decorativeCase')}</span>
                            <small>{getSettingPrice('storDekoratifKasa')} ₸</small>
                          </div>
                        </label>
                      </div>
                      <div className="form-group order-extra-checkbox-item">
                        <label className="order-extra-checkbox-label">
                          <input type="checkbox" checked={newOrder.hasStorAltEtekBeyazIs || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasStorAltEtekBeyazIs', checked); onFieldChange('storAltEtekBeyazIsAmount', checked ? getSettingPrice('storAltEtekBeyazIs') : 0); }} />
                          <div>
                            <span>{of('extras.bottomHemWhiteWork')}</span>
                            <small>{getSettingPrice('storAltEtekBeyazIs')} ₸</small>
                          </div>
                        </label>
                      </div>
                    </>
                  )}

                  {selectedProductType === 'zebra' && (
                    <>
                      <div className="form-group order-extra-checkbox-item">
                        <label className="order-extra-checkbox-label">
                          <input type="checkbox" checked={newOrder.hasZebraJumbo || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasZebraJumbo', checked); onFieldChange('zebraJumboAmount', checked ? getSettingPrice('zebraJumbo') : 0); }} />
                          <div>
                            <span>{of('extras.jumboSystemZebra')}</span>
                            <small>{getSettingPrice('zebraJumbo')} ₸</small>
                          </div>
                        </label>
                      </div>
                      <div className="form-group order-extra-checkbox-item">
                        <label className="order-extra-checkbox-label">
                          <input type="checkbox" checked={newOrder.hasZebraAltEtekDilim || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasZebraAltEtekDilim', checked); onFieldChange('zebraAltEtekDilimAmount', checked ? getSettingPrice('zebraAltEtekDilim') : 0); }} />
                          <div>
                            <span>{of('extras.bottomHemSliceZebra')}</span>
                            <small>{getSettingPrice('zebraAltEtekDilim')} ₸</small>
                          </div>
                        </label>
                      </div>
                      <div className="form-group order-extra-checkbox-item">
                        <label className="order-extra-checkbox-label">
                          <input type="checkbox" checked={newOrder.hasZebraKapaliKasa || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasZebraKapaliKasa', checked); onFieldChange('zebraKapaliKasaAmount', checked ? getSettingPrice('zebraKapaliKasa') : 0); }} />
                          <div>
                            <span>{of('extras.closedCaseZebra')}</span>
                            <small>{getSettingPrice('zebraKapaliKasa')} ₸</small>
                          </div>
                        </label>
                      </div>
                    </>
                  )}
                </div>


                {newOrder.totalPrice && (
                  <div className="form-group">
                    <label>{of('labels.totalAmount')}</label>
                    <input
                      type="text"
                      value={formatPrice(newOrder.totalPrice)}
                      readOnly
                      className="total-price"
                    />
                  </div>
                )}

                {newOrder.discountedPrice && parseFloat(newOrder.discount) > 0 && (
                  <div className="form-group">
                    <label>{of('labels.discountedAmount')}</label>
                    <input
                      type="text"
                      value={formatPrice(newOrder.discountedPrice)}
                      readOnly
                      className="discounted-price"
                    />
                  </div>
                )}
              </>
            )}

            {/* Ahşap Jaluzi için özel alanlar */}
            {selectedProductType === 'ahsapJaluzi' && (
              <>
                {!isWholesale && (
                <div className="form-group">
                  <label>{of('labels.roomName')}</label>
                  <input type="text" value={newOrder.roomName} onChange={(e) => onFieldChange('roomName', e.target.value)} disabled={isLoading} />
                </div>
                )}
                <div className="form-group">
                  <label>{of('labels.productName')}</label>
                  <CreatableSelect
                    options={productOptions}
                    value={
                      productOptions.find(o => o.value === (productItems.find(p => p.name === newOrder.productName)?.id)) ||
                      (newOrder.productName ? { value: newOrder.productName, label: newOrder.productName } : null)
                    }
                    onChange={(option) => {
                      if (!option) {
                        onFieldChange('productName', '');
                        onFieldChange('unitPrice', '');
                      } else if (option.__isNew__) {
                        onFieldChange('productName', option.value);
                        onFieldChange('unitPrice', '');
                      } else {
                        handleProductSelect(option.value);
                      }
                    }}
                    isDisabled={isLoading}
                    isClearable
                    isSearchable
                    placeholder={of('placeholders.selectProduct')}
                    noOptionsMessage={() => of('messages.noProduct')}
                    formatCreateLabel={(inputValue) => of('placeholders.createFormat', { value: inputValue })}
                    styles={customSelectStyles}
                  />
                </div>
                <div className="form-group">
                  <label>{of('labels.productVariant')}</label>
                  <input
                    type="text"
                    value={newOrder.productVariant || ''}
                    onChange={(e) => onFieldChange('productVariant', e.target.value)}
                    disabled={isLoading}
                    placeholder={of('placeholders.productVariant')}
                  />
                </div>
                <div className="form-group">
                  <label>{of('labels.systemType')}</label>
                  <Select
                    options={Object.entries(JALUZI_SISTEM_TIPLERI).map(([key, value]) => ({
                      value,
                      label: localizedJaluziSistemTipleri[key] || value
                    }))}
                    value={newOrder.sistemTipi ? {
                      value: newOrder.sistemTipi,
                      label: localizedJaluziSistemTipleri[
                        Object.keys(JALUZI_SISTEM_TIPLERI).find((key) => JALUZI_SISTEM_TIPLERI[key] === newOrder.sistemTipi)
                      ] || newOrder.sistemTipi
                    } : null}
                    onChange={(option) => {
                      const selectedSys = option ? option.value : '';
                      onFieldChange('sistemTipi', selectedSys);
                      const rawProduct = productItems.find(p => p.name === newOrder.productName);
                      const product = rawProduct ? convertProductToKztForOrder(rawProduct, exchangeRates) : null;
                      if (product) {
                        const { price, cost } = resolveJaluziOrderPricing(product, selectedSys, isWholesale);
                        onFieldChange('baseUnitPrice', price);
                        onFieldChange('unitPrice', price);
                        onFieldChange('baseUnitCost', cost);
                        onFieldChange('unitCost', cost);
                      }
                    }}
                    isDisabled={isLoading} isClearable isSearchable placeholder={of('placeholders.selectSystemType')} noOptionsMessage={() => of('messages.noSystemType')} filterOption={(option, inputValue) => option.label.toLowerCase().includes(inputValue.toLowerCase())} styles={customSelectStyles}
                  />
                </div>
                <div className="form-group">
                  <label>{of('labels.widthCm')}</label>
                  <input type="number" step="0.01" onKeyDown={handleNumericKeyDown} value={newOrder.width} onChange={(e) => onFieldChange('width', e.target.value)} required disabled={isLoading} />
                </div>
                <div className="form-group">
                  <label>{of('labels.heightCm')}</label>
                  <input type="number" step="0.01" onKeyDown={handleNumericKeyDown} value={newOrder.height} onChange={(e) => onFieldChange('height', e.target.value)} required disabled={isLoading} />
                </div>
                {newOrder.usedM2 && (
                  <div className="form-group">
                    <label>{of('labels.usedMt2')}</label>
                    <input type="text" value={newOrder.usedM2} readOnly className="readonly-field" />
                  </div>
                )}
                <div className="form-group">
                  <label>{of('labels.direction')}</label>
                  <select value={newOrder.direction || ''} onChange={(e) => onFieldChange('direction', e.target.value)} required disabled={isLoading}>
                    <option value="">{of('placeholders.select')}</option>
                    {Object.entries(DIRECTION_TYPES).map(([key, value]) => (<option key={key} value={value}>{localizedDirectionTypes[key]}</option>))}
                  </select>
                </div>
                <div className="form-group">
                  <label>{of('labels.motor')}</label>
                  <select value={newOrder.motorType || ''} onChange={(e) => onFieldChange('motorType', e.target.value)} disabled={isLoading}>
                    <option value="">{of('placeholders.select')}</option>
                    {Object.entries(MOTOR_TYPES).map(([key, value]) => (<option key={key} value={value}>{localizedMotorTypes[key]}</option>))}
                  </select>
                </div>
                {newOrder.motorType && (
                  <div className="form-group">
                    <label>{of('labels.motorFee', { currency: getMotorCurrency() })}</label>
                    <input type="number" step="0.01" value={newOrder.motorPriceCurrency || ''} onChange={(e) => onFieldChange('motorPriceCurrency', e.target.value)} disabled={isLoading} />
                    {newOrder.motorPriceCurrency && <small style={{ display: 'block', marginTop: '5px', color: '#b04309' }}>≈ {newOrder.motorPrice ? Number(newOrder.motorPrice).toFixed(2) : 0} ₺</small>}
                  </div>
                )}
                <div className="form-group">
                  <label>&nbsp;</label>
                  <label style={{ display: 'flex', alignItems: 'center', height: '42px', padding: '0 12px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '100%', backgroundColor: '#f8fafc', cursor: 'pointer', margin: 0, transition: 'all 0.2s ease' }} onMouseOver={(e) => { e.currentTarget.style.borderColor = '#94a3b8'; e.currentTarget.style.backgroundColor = '#f1f5f9' }} onMouseOut={(e) => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.backgroundColor = '#f8fafc' }}>
                    <input type="checkbox" style={{ width: '16px', height: '16px', marginRight: '10px', cursor: 'pointer', accentColor: '#b04309' }} checked={newOrder.hasZigil || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasZigil', checked); onFieldChange('zigilAmount', checked ? getSettingPrice('zigil') : 0); }} />
                    <span style={{ fontWeight: '500', fontSize: '13px', color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{of('extras.woodZigilSystem')}</span>
                  </label>
                </div>
                <div className="form-group">
                  <div className="label-with-icon">
                    <label>{of('labels.unitPrice')}</label>
                    <button type="button" className={`lock-btn ${showBaseCost ? 'active' : ''}`} onClick={() => setShowBaseCost(!showBaseCost)} title={of('titles.costInfo')}><i className={`fas ${showBaseCost ? 'fa-lock-open' : 'fa-lock'}`}></i></button>
                  </div>
                  <input type="number" step="0.01" min="0.01" onKeyDown={handleNumericKeyDown} value={newOrder.unitPrice} onChange={(e) => { onFieldChange('unitPrice', e.target.value); onFieldChange('baseUnitPrice', e.target.value); onFieldChange('baseUnitCost', ''); onFieldChange('unitCost', ''); }} required disabled={isLoading} />
                </div>
                {showBaseCost && (<div className="form-group"><label className="extra-cost-label">{of('labels.unitCost')}</label><input type="number" step="0.01" min="0" value={newOrder.unitCost || ''} onKeyDown={handleNumericKeyDown} onChange={(e) => { onFieldChange('unitCost', e.target.value); onFieldChange('baseUnitCost', e.target.value); }} disabled={isLoading} placeholder={of('placeholders.hiddenCost')} /></div>)}
                <div className="form-group">
                  <div className="label-with-icon">
                    <label>{of('labels.mounting')}</label>
                    <button
                      type="button"
                      className={`lock-btn ${showMountingCost ? 'active' : ''}`}
                      onClick={() => setShowMountingCost(!showMountingCost)}
                      title={of('titles.mountingCostEntry')}
                    >
                      <i className={`fas ${showMountingCost ? 'fa-lock-open' : 'fa-lock'}`}></i>
                    </button>
                  </div>
                  <input type="number" step="0.01" value={newOrder.mounting} onChange={(e) => onFieldChange('mounting', e.target.value)} disabled={isLoading} />
                </div>
                {showMountingCost && (
                  <div className="form-group">
                    <label className="extra-cost-label">{of('labels.mountingCost')}</label>
                    <input type="number" step="0.01" value={newOrder.mountingCost || ''} onKeyDown={handleNumericKeyDown} onChange={(e) => onFieldChange('mountingCost', e.target.value)} disabled={isLoading} placeholder={of('placeholders.hiddenCost')} />
                  </div>
                )}

                <div className="form-group">
                  <label>{of('labels.quantityRequired')}</label>
                  <input type="number" step="1" min="1" onKeyDown={handleNumericKeyDown} value={newOrder.quantity || ''} onChange={(e) => onFieldChange('quantity', e.target.value)} required disabled={isLoading} />
                </div>
                <div className="form-group">
                  <label>{of('labels.discount')}</label>
                  <input type="number" step="0.01" min="0" max="100" onKeyDown={handleNumericKeyDown} value={newOrder.discount} onChange={(e) => onFieldChange('discount', e.target.value)} disabled={isLoading} />
                </div>
                {newOrder.totalPrice && (
                  <div className="form-group">
                    <label>{of('labels.totalAmount')}</label>
                    <input type="text" value={formatPrice(newOrder.totalPrice)} readOnly className="total-price" />
                  </div>
                )}
                {newOrder.discountedPrice && parseFloat(newOrder.discount) > 0 && (
                  <div className="form-group">
                    <label>{of('labels.discountedAmount')}</label>
                    <input type="text" value={formatPrice(newOrder.discountedPrice)} readOnly className="discounted-price" />
                  </div>
                )}
              </>
            )}

            {/* Metal Jaluzi için özel alanlar */}
            {selectedProductType === 'metalJaluzi' && (
              <>
                {!isWholesale && (
                <div className="form-group">
                  <label>{of('labels.roomName')}</label>
                  <input type="text" value={newOrder.roomName} onChange={(e) => onFieldChange('roomName', e.target.value)} disabled={isLoading} />
                </div>
                )}
                <div className="form-group">
                  <label>{of('labels.productName')}</label>
                  <CreatableSelect
                    options={productOptions}
                    value={
                      productOptions.find(o => o.value === (productItems.find(p => p.name === newOrder.productName)?.id)) ||
                      (newOrder.productName ? { value: newOrder.productName, label: newOrder.productName } : null)
                    }
                    onChange={(option) => {
                      if (!option) {
                        onFieldChange('productName', '');
                        onFieldChange('unitPrice', '');
                      } else if (option.__isNew__) {
                        onFieldChange('productName', option.value);
                        onFieldChange('unitPrice', '');
                      } else {
                        handleProductSelect(option.value);
                      }
                    }}
                    isDisabled={isLoading}
                    isClearable
                    isSearchable
                    placeholder={of('placeholders.selectProduct')}
                    noOptionsMessage={() => of('messages.noProduct')}
                    formatCreateLabel={(inputValue) => of('placeholders.createFormat', { value: inputValue })}
                    styles={customSelectStyles}
                  />
                </div>
                <div className="form-group">
                  <label>{of('labels.productVariant')}</label>
                  <input
                    type="text"
                    value={newOrder.productVariant || ''}
                    onChange={(e) => onFieldChange('productVariant', e.target.value)}
                    disabled={isLoading}
                    placeholder={of('placeholders.productVariant')}
                  />
                </div>
                <div className="form-group">
                  <label>{of('labels.widthCm')}</label>
                  <input type="number" step="0.01" onKeyDown={handleNumericKeyDown} value={newOrder.width} onChange={(e) => onFieldChange('width', e.target.value)} required disabled={isLoading} />
                </div>
                <div className="form-group">
                  <label>{of('labels.heightCm')}</label>
                  <input type="number" step="0.01" onKeyDown={handleNumericKeyDown} value={newOrder.height} onChange={(e) => onFieldChange('height', e.target.value)} required disabled={isLoading} />
                </div>
                {newOrder.usedM2 && (
                  <div className="form-group">
                    <label>{of('labels.usedMt2')}</label>
                    <input type="text" value={newOrder.usedM2} readOnly className="readonly-field" />
                  </div>
                )}
                <div className="form-group">
                  <label>{of('labels.direction')}</label>
                  <select value={newOrder.direction || ''} onChange={(e) => onFieldChange('direction', e.target.value)} required disabled={isLoading}>
                    <option value="">{of('placeholders.select')}</option>
                    {Object.entries(DIRECTION_TYPES).map(([key, value]) => (<option key={key} value={value}>{localizedDirectionTypes[key]}</option>))}
                  </select>
                </div>
                <div className="form-group">
                  <label>{of('labels.motor')}</label>
                  <select value={newOrder.motorType || ''} onChange={(e) => onFieldChange('motorType', e.target.value)} disabled={isLoading}>
                    <option value="">{of('placeholders.select')}</option>
                    {Object.entries(MOTOR_TYPES).map(([key, value]) => (<option key={key} value={value}>{localizedMotorTypes[key]}</option>))}
                  </select>
                </div>
                {newOrder.motorType && (
                  <div className="form-group">
                    <label>{of('labels.motorFee', { currency: getMotorCurrency() })}</label>
                    <input type="number" step="0.01" value={newOrder.motorPriceCurrency || ''} onChange={(e) => onFieldChange('motorPriceCurrency', e.target.value)} disabled={isLoading} />
                    {newOrder.motorPriceCurrency && <small style={{ display: 'block', marginTop: '5px', color: '#b04309' }}>≈ {newOrder.motorPrice ? Number(newOrder.motorPrice).toFixed(2) : 0} ₺</small>}
                  </div>
                )}
                <div className="form-group">
                  <label>&nbsp;</label>
                  <label style={{ display: 'flex', alignItems: 'center', height: '42px', padding: '0 12px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '100%', backgroundColor: '#f8fafc', cursor: 'pointer', margin: 0, transition: 'all 0.2s ease' }} onMouseOver={(e) => { e.currentTarget.style.borderColor = '#94a3b8'; e.currentTarget.style.backgroundColor = '#f1f5f9' }} onMouseOut={(e) => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.backgroundColor = '#f8fafc' }}>
                    <input type="checkbox" style={{ width: '16px', height: '16px', marginRight: '10px', cursor: 'pointer', accentColor: '#b04309' }} checked={newOrder.hasMetalZigil || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasMetalZigil', checked); onFieldChange('metalZigilAmount', checked ? getSettingPrice('metalZigil') : 0); }} />
                    <span style={{ fontWeight: '500', fontSize: '13px', color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{of('extras.metalZigilWireKit')}</span>
                  </label>
                </div>
                <div className="form-group">
                  <label>&nbsp;</label>
                  <label style={{ display: 'flex', alignItems: 'center', height: '42px', padding: '0 12px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '100%', backgroundColor: '#f8fafc', cursor: 'pointer', margin: 0, transition: 'all 0.2s ease' }} onMouseOver={(e) => { e.currentTarget.style.borderColor = '#94a3b8'; e.currentTarget.style.backgroundColor = '#f1f5f9' }} onMouseOut={(e) => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.backgroundColor = '#f8fafc' }}>
                    <input type="checkbox" style={{ width: '16px', height: '16px', marginRight: '10px', cursor: 'pointer', accentColor: '#b04309' }} checked={newOrder.hasMetalReduktor || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasMetalReduktor', checked); const baseP = Number(newOrder.baseUnitPrice) || 0; const baseC = Number(newOrder.baseUnitCost) || 0; const extraRedP = checked ? Number(getSettingPrice('metalReduktor')) : 0; const extraRedC = checked ? Number(getSettingCost('metalReduktor')) : 0; onFieldChange('unitPrice', baseP + extraRedP); onFieldChange('unitCost', baseC + extraRedC); }} />
                    <span style={{ fontWeight: '500', fontSize: '13px', color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{of('extras.reducerCurtainDiff')}</span>
                  </label>
                </div>
                <div className="form-group">
                  <label>&nbsp;</label>
                  <label style={{ display: 'flex', alignItems: 'center', height: '42px', padding: '0 12px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '100%', backgroundColor: '#f8fafc', cursor: 'pointer', margin: 0, transition: 'all 0.2s ease' }} onMouseOver={(e) => { e.currentTarget.style.borderColor = '#94a3b8'; e.currentTarget.style.backgroundColor = '#f1f5f9' }} onMouseOut={(e) => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.backgroundColor = '#f8fafc' }}>
                    <input type="checkbox" style={{ width: '16px', height: '16px', marginRight: '10px', cursor: 'pointer', accentColor: '#b04309' }} checked={newOrder.hasIkiCamArasi || false} onChange={(e) => { const checked = e.target.checked; onFieldChange('hasIkiCamArasi', checked); onFieldChange('ikiCamArasiAmount', checked ? getSettingPrice('ikiCamArasi') : 0); }} />
                    <span style={{ fontWeight: '500', fontSize: '13px', color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{of('extras.betweenGlassButton')}</span>
                  </label>
                </div>
                <div className="form-group">
                  <div className="label-with-icon">
                    <label>{of('labels.unitPrice')}</label>
                    <button type="button" className={`lock-btn ${showBaseCost ? 'active' : ''}`} onClick={() => setShowBaseCost(!showBaseCost)} title={of('titles.costInfo')}><i className={`fas ${showBaseCost ? 'fa-lock-open' : 'fa-lock'}`}></i></button>
                  </div>
                  <input type="number" step="0.01" min="0.01" onKeyDown={handleNumericKeyDown} value={newOrder.unitPrice} onChange={(e) => { 
                    onFieldChange('unitPrice', e.target.value); 
                    onFieldChange('baseUnitPrice', e.target.value); 
                    onFieldChange('baseUnitCost', ''); 
                    onFieldChange('unitCost', '');
                    if (newOrder.hasMetalReduktor) onFieldChange('hasMetalReduktor', false);
                  }} required disabled={isLoading} />
                </div>
                {showBaseCost && (<div className="form-group"><label className="extra-cost-label">{of('labels.unitCost')}</label><input type="number" step="0.01" min="0" value={newOrder.unitCost || ''} onKeyDown={handleNumericKeyDown} onChange={(e) => { 
                  onFieldChange('unitCost', e.target.value); 
                  onFieldChange('baseUnitCost', e.target.value); 
                  if (newOrder.hasMetalReduktor) {
                    onFieldChange('hasMetalReduktor', false);
                    onFieldChange('unitPrice', newOrder.baseUnitPrice);
                  }
                }} disabled={isLoading} placeholder={of('placeholders.hiddenCost')} /></div>)}
                <div className="form-group">
                  <div className="label-with-icon">
                    <label>{of('labels.mounting')}</label>
                    <button
                      type="button"
                      className={`lock-btn ${showMountingCost ? 'active' : ''}`}
                      onClick={() => setShowMountingCost(!showMountingCost)}
                      title={of('titles.mountingCostEntry')}
                    >
                      <i className={`fas ${showMountingCost ? 'fa-lock-open' : 'fa-lock'}`}></i>
                    </button>
                  </div>
                  <input type="number" step="0.01" value={newOrder.mounting} onChange={(e) => onFieldChange('mounting', e.target.value)} disabled={isLoading} />
                </div>
                {showMountingCost && (
                  <div className="form-group">
                    <label className="extra-cost-label">{of('labels.mountingCost')}</label>
                    <input type="number" step="0.01" value={newOrder.mountingCost || ''} onKeyDown={handleNumericKeyDown} onChange={(e) => onFieldChange('mountingCost', e.target.value)} disabled={isLoading} placeholder={of('placeholders.hiddenCost')} />
                  </div>
                )}
                <div className="form-group">
                  <label>{of('labels.quantityRequired')}</label>
                  <input type="number" step="1" min="1" onKeyDown={handleNumericKeyDown} value={newOrder.quantity || ''} onChange={(e) => onFieldChange('quantity', e.target.value)} required disabled={isLoading} />
                </div>
                <div className="form-group">
                  <label>{of('labels.discount')}</label>
                  <input type="number" step="0.01" min="0" max="100" onKeyDown={handleNumericKeyDown} value={newOrder.discount} onChange={(e) => onFieldChange('discount', e.target.value)} disabled={isLoading} />
                </div>
                {newOrder.totalPrice && (
                  <div className="form-group">
                    <label>{of('labels.totalAmount')}</label>
                    <input type="text" value={formatPrice(newOrder.totalPrice)} readOnly className="total-price" />
                  </div>
                )}
                {newOrder.discountedPrice && parseFloat(newOrder.discount) > 0 && (
                  <div className="form-group">
                    <label>{of('labels.discountedAmount')}</label>
                    <input type="text" value={formatPrice(newOrder.discountedPrice)} readOnly className="discounted-price" />
                  </div>
                )}
              </>
            )}

            {/* Dikey PVC ve Dikey Stor için özel alanlar */}
            {(selectedProductType === 'dikeyPvc' || selectedProductType === 'dikeyStor') && (
              <>
                {!isWholesale && (
                <div className="form-group">
                  <label>{of('labels.roomName')}</label>
                  <input
                    type="text"
                    value={newOrder.roomName}
                    onChange={(e) => onFieldChange('roomName', e.target.value)}
                    disabled={isLoading}
                  />
                </div>
                )}

                <div className="form-group">
                  <label>{of('labels.productName')}</label>
                  <CreatableSelect
                    options={productOptions}
                    value={
                      productOptions.find(o => o.value === (productItems.find(p => p.name === newOrder.productName)?.id)) ||
                      (newOrder.productName ? { value: newOrder.productName, label: newOrder.productName } : null)
                    }
                    onChange={(option) => {
                      if (!option) {
                        onFieldChange('productName', '');
                        onFieldChange('unitPrice', '');
                      } else if (option.__isNew__) {
                        onFieldChange('productName', option.value);
                        onFieldChange('unitPrice', '');
                      } else {
                        handleProductSelect(option.value);
                      }
                    }}
                    isDisabled={isLoading}
                    isClearable
                    isSearchable
                    placeholder={of('placeholders.selectProduct')}
                    noOptionsMessage={() => of('messages.noProduct')}
                    formatCreateLabel={(inputValue) => of('placeholders.createFormat', { value: inputValue })}
                    styles={customSelectStyles}
                  />
                </div>

                <div className="form-group">
                  <label>{of('labels.productVariant')}</label>
                  <input
                    type="text"
                    value={newOrder.productVariant || ''}
                    onChange={(e) => onFieldChange('productVariant', e.target.value)}
                    disabled={isLoading}
                    placeholder={of('placeholders.productVariantExtra')}
                  />
                </div>

                <div className="form-group">
                  <label>{of('labels.widthCm')}</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newOrder.width}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('width', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>{of('labels.heightCm')}</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newOrder.height}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('height', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>{of('labels.direction')}</label>
                  <select
                    value={newOrder.direction || ''}
                    onChange={(e) => onFieldChange('direction', e.target.value)}
                    required
                    disabled={isLoading}
                  >
                    <option value="">{of('placeholders.select')}</option>
                    {Object.entries(DIKEY_DIRECTION_TYPES).map(([key, value]) => (
                      <option key={key} value={value}>{localizedDikeyDirectionTypes[key]}</option>
                    ))}
                  </select>
                </div>

                {newOrder.usedM2 && (
                  <div className="form-group">
                    <label>{of('labels.usedMt2')}</label>
                    <input
                      type="text"
                      value={newOrder.usedM2}
                      readOnly
                      className="readonly-field"
                    />
                  </div>
                )}

                <div className="form-group">
                  <div className="label-with-icon">
                    <label>{of('labels.unitPrice')}</label>
                    <button
                      type="button"
                      className={`lock-btn ${showBaseCost ? 'active' : ''}`}
                      onClick={() => setShowBaseCost(!showBaseCost)}
                      title={of('titles.costInfo')}
                    >
                      <i className={`fas ${showBaseCost ? 'fa-lock-open' : 'fa-lock'}`}></i>
                    </button>
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={newOrder.unitPrice}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => {
                      onFieldChange('unitPrice', e.target.value);
                      onFieldChange('baseUnitPrice', e.target.value);
                      onFieldChange('baseUnitCost', '');
                      onFieldChange('unitCost', '');
                    }}
                    required
                    disabled={isLoading}
                  />
                </div>

                {showBaseCost && (
                  <div className="form-group">
                    <label className="extra-cost-label">{of('labels.unitCost')}</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newOrder.unitCost || ''}
                      onKeyDown={handleNumericKeyDown}
                      onChange={(e) => {
                        onFieldChange('unitCost', e.target.value);
                        onFieldChange('baseUnitCost', e.target.value);
                        if (newOrder.hasDikeyFon) {
                          onFieldChange('hasDikeyFon', false);
                          onFieldChange('unitPrice', newOrder.baseUnitPrice);
                        }
                      }}
                      disabled={isLoading}
                      placeholder={of('placeholders.hiddenCost')}
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>{of('labels.motor')}</label>
                  <select
                    value={newOrder.motorType || ''}
                    onChange={(e) => onFieldChange('motorType', e.target.value)}
                    disabled={isLoading}
                  >
                    <option value="">{of('placeholders.select')}</option>
                    {Object.entries(MOTOR_TYPES).map(([key, value]) => (
                      <option key={key} value={value}>{localizedMotorTypes[key]}</option>
                    ))}
                  </select>
                </div>

                {newOrder.motorType && (
                  <div className="form-group">
                    <label>{of('labels.motorFee', { currency: getMotorCurrency() })}</label>
                    <input
                      type="number"
                      step="0.01"
                      value={newOrder.motorPriceCurrency || ''}
                      onKeyDown={handleNumericKeyDown}
                      onChange={(e) => onFieldChange('motorPriceCurrency', e.target.value)}
                      disabled={isLoading}
                    />
                    {newOrder.motorPriceCurrency && (
                      <small style={{ display: 'block', marginTop: '5px', color: '#b04309' }}>
                        ≈ {newOrder.motorPrice ? Number(newOrder.motorPrice).toFixed(2) : 0} ₺
                      </small>
                    )}
                  </div>
                )}

                <div className="form-group">
                  <div className="label-with-icon">
                    <label>{of('labels.mounting')}</label>
                    <button
                      type="button"
                      className={`lock-btn ${showMountingCost ? 'active' : ''}`}
                      onClick={() => setShowMountingCost(!showMountingCost)}
                      title={of('titles.mountingCostEntry')}
                    >
                      <i className={`fas ${showMountingCost ? 'fa-lock-open' : 'fa-lock'}`}></i>
                    </button>
                  </div>

                  <input
                    type="number"
                    step="0.01"
                    value={newOrder.mounting}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('mounting', e.target.value)}
                    disabled={isLoading}
                  />
                </div>
                {showMountingCost && (
                  <div className="form-group">
                    <label className="extra-cost-label">{of('labels.mountingCost')}</label>
                    <input
                      type="number"
                      step="0.01"
                      value={newOrder.mountingCost || ''}
                      onKeyDown={handleNumericKeyDown}
                      onChange={(e) => onFieldChange('mountingCost', e.target.value)}
                      disabled={isLoading}
                      placeholder={of('placeholders.hiddenCost')}
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>{of('labels.quantityRequired')}</label>
                  <input
                    type="number"
                    step="1"
                    min="1"
                    value={newOrder.quantity || ''}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('quantity', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>{of('labels.discount')}</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={newOrder.discount}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('discount', e.target.value)}
                    disabled={isLoading}
                  />
                </div>

                {newOrder.totalPrice && (
                  <div className="form-group">
                    <label>{of('labels.totalAmount')}</label>
                    <input
                      type="text"
                      value={formatPrice(newOrder.totalPrice)}
                      readOnly
                      className="total-price"
                    />
                  </div>
                )}

                {newOrder.discountedPrice && parseFloat(newOrder.discount) > 0 && (
                  <div className="form-group">
                    <label>{of('labels.discountedAmount')}</label>
                    <input
                      type="text"
                      value={formatPrice(newOrder.discountedPrice)}
                      readOnly
                      className="discounted-price"
                    />
                  </div>
                )}
              </>
            )}

            {/* Dikey Perde için özel alanlar */}
            {selectedProductType === 'dikeyPerde' && (
              <>
                {!isWholesale && (
                <div className="form-group">
                  <label>{of('labels.roomName')}</label>
                  <input
                    type="text"
                    value={newOrder.roomName}
                    onChange={(e) => onFieldChange('roomName', e.target.value)}
                    disabled={isLoading}
                  />
                </div>
                )}

                <div className="form-group">
                  <label>{of('labels.productName')}</label>
                  <CreatableSelect
                    options={productOptions}
                    value={
                      productOptions.find(o => o.value === (productItems.find(p => p.name === newOrder.productName)?.id)) ||
                      (newOrder.productName ? { value: newOrder.productName, label: newOrder.productName } : null)
                    }
                    onChange={(option) => {
                      if (!option) {
                        onFieldChange('productName', '');
                        onFieldChange('unitPrice', '');
                      } else if (option.__isNew__) {
                        onFieldChange('productName', option.value);
                        onFieldChange('unitPrice', '');
                      } else {
                        handleProductSelect(option.value);
                      }
                    }}
                    isDisabled={isLoading}
                    isClearable
                    isSearchable
                    placeholder={of('placeholders.selectProduct')}
                    noOptionsMessage={() => of('messages.noProduct')}
                    formatCreateLabel={(inputValue) => of('placeholders.createFormat', { value: inputValue })}
                    styles={customSelectStyles}
                  />
                </div>

                <div className="form-group">
                  <label>{of('labels.productVariant')}</label>
                  <input
                    type="text"
                    value={newOrder.productVariant || ''}
                    onChange={(e) => onFieldChange('productVariant', e.target.value)}
                    disabled={isLoading}
                    placeholder={of('placeholders.productVariantExample')}
                  />
                </div>

                <div className="form-group">
                  <label>{of('labels.widthCm')}</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newOrder.width}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('width', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>{of('labels.heightCm')}</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newOrder.height}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('height', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>{of('labels.direction')}</label>
                  <select
                    value={newOrder.direction || ''}
                    onChange={(e) => onFieldChange('direction', e.target.value)}
                    required
                    disabled={isLoading}
                  >
                    <option value="">{of('placeholders.select')}</option>
                    {Object.entries(DIKEY_DIRECTION_TYPES).map(([key, value]) => (
                      <option key={key} value={value}>{localizedDikeyDirectionTypes[key]}</option>
                    ))}
                  </select>
                </div>

                {newOrder.usedM2 && (
                  <div className="form-group">
                    <label>{of('labels.usedMt2')}</label>
                    <input
                      type="text"
                      value={newOrder.usedM2}
                      readOnly
                      className="readonly-field"
                    />
                  </div>
                )}

                <div className="form-group">
                  <div className="label-with-icon">
                    <label>{of('labels.unitPrice')}</label>
                    <button
                      type="button"
                      className={`lock-btn ${showBaseCost ? 'active' : ''}`}
                      onClick={() => setShowBaseCost(!showBaseCost)}
                      title={of('titles.costInfo')}
                    >
                      <i className={`fas ${showBaseCost ? 'fa-lock-open' : 'fa-lock'}`}></i>
                    </button>
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={newOrder.unitPrice}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => {
                      onFieldChange('unitPrice', e.target.value);
                      onFieldChange('baseUnitPrice', e.target.value);
                      onFieldChange('baseUnitCost', '');
                      onFieldChange('unitCost', '');
                      if (newOrder.hasDikeyFon) onFieldChange('hasDikeyFon', false);
                    }}
                    required
                    disabled={isLoading}
                  />
                </div>

                {showBaseCost && (
                  <div className="form-group">
                    <label className="extra-cost-label">{of('labels.unitCost')}</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newOrder.unitCost || ''}
                      onKeyDown={handleNumericKeyDown}
                      onChange={(e) => {
                        onFieldChange('unitCost', e.target.value);
                        onFieldChange('baseUnitCost', e.target.value);
                        if (newOrder.hasDikeyFon) {
                          onFieldChange('hasDikeyFon', false);
                          onFieldChange('unitPrice', newOrder.baseUnitPrice);
                        }
                      }}
                      disabled={isLoading}
                      placeholder={of('placeholders.hiddenCost')}
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>&nbsp;</label>
                  <label
                    style={{
                      display: 'flex', alignItems: 'center', height: '42px', padding: '0 12px',
                      border: '1px solid #cbd5e1', borderRadius: '6px', width: '100%',
                      backgroundColor: !newOrder.unitPrice ? '#f1f5f9' : '#f8fafc',
                      cursor: !newOrder.unitPrice ? 'not-allowed' : 'pointer',
                      margin: 0, transition: 'all 0.2s ease',
                      opacity: !newOrder.unitPrice ? 0.6 : 1
                    }}
                    onMouseOver={(e) => {
                      if (newOrder.unitPrice) {
                        e.currentTarget.style.borderColor = '#94a3b8';
                        e.currentTarget.style.backgroundColor = '#f1f5f9';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (newOrder.unitPrice) {
                        e.currentTarget.style.borderColor = '#cbd5e1';
                        e.currentTarget.style.backgroundColor = '#f8fafc';
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      disabled={!newOrder.unitPrice || isLoading}
                      style={{
                        width: '16px', height: '16px', marginRight: '10px',
                        cursor: !newOrder.unitPrice ? 'not-allowed' : 'pointer',
                        accentColor: '#b04309'
                      }}
                      checked={newOrder.hasDikeyFon || false}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        onFieldChange('hasDikeyFon', checked);
                        
                        const fonPrice = Number(getSettingPrice('dikeyFon')) || 0;
                        const fonCost = Number(getSettingCost('dikeyFon')) || 0;
                        
                        // Amount alanını sadece bilgi amaçlı güncelleyelim
                        onFieldChange('dikeyFonAmount', checked ? fonPrice : 0);

                        const baseP = parseFloat(newOrder.baseUnitPrice) || 0;
                        const baseC = parseFloat(newOrder.baseUnitCost) || 0;
                        
                        // Birim fiyat ve maliyete ekle/çıkar (base üzerinden)
                        const newUnitP = checked ? baseP + fonPrice : baseP;
                        const newUnitC = checked ? baseC + fonCost : baseC;
                        
                        onFieldChange('unitPrice', newUnitP > 0 ? newUnitP.toFixed(2) : '');
                        onFieldChange('unitCost', newUnitC > 0 ? newUnitC.toFixed(2) : '');
                        onFieldChange('baseUnitCost', baseC > 0 ? baseC.toFixed(2) : '');

                        // Fon sayısı seçiliyse varsayılan olarak 1 atayalım
                        if (checked && !newOrder.dikeyFonCount) {
                          onFieldChange('dikeyFonCount', 1);
                        }
                      }}
                    />
                    <span style={{ fontWeight: '500', fontSize: '13px', color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Fon (+{getSettingPrice('dikeyFon')} ₺)</span>
                  </label>
                </div>

                {newOrder.hasDikeyFon && (
                  <>
                    <div className="form-group">
                      <label>{of('labels.liningName')}</label>
                      {adminSettings.dikeyFonList && adminSettings.dikeyFonList.length > 0 ? (
                        <Select
                          options={adminSettings.dikeyFonList.map(f => ({ value: f, label: f }))}
                          value={newOrder.dikeyFonName ? { value: newOrder.dikeyFonName, label: newOrder.dikeyFonName } : null}
                          onChange={(option) => onFieldChange('dikeyFonName', option ? option.value : '')}
                          isDisabled={isLoading}
                          isClearable
                          isSearchable
                          placeholder={of('placeholders.selectFabric')}
                          noOptionsMessage={() => of('messages.noFabric')}
                          styles={customSelectStyles}
                        />
                      ) : (
                        <input
                          type="text"
                          value={newOrder.dikeyFonName || ''}
                          onChange={(e) => onFieldChange('dikeyFonName', e.target.value)}
                          disabled={isLoading}
                          placeholder={of('placeholders.enterFabricName')}
                        />
                      )}
                    </div>
                    <div className="form-group">
                      <label>{of('labels.liningCount')}</label>
                      <input
                        type="number"
                        step="1"
                        min="1"
                        value={newOrder.dikeyFonCount || ''}
                        onChange={(e) => {
                          let val = e.target.value;
                          if (val !== '' && parseInt(val) < 1) val = '1';
                          onFieldChange('dikeyFonCount', val);
                        }}
                        disabled={isLoading}
                      />
                    </div>
                  </>
                )}

                <div className="form-group">
                  <label>{of('labels.motor')}</label>
                  <select
                    value={newOrder.motorType || ''}
                    onChange={(e) => onFieldChange('motorType', e.target.value)}
                    disabled={isLoading}
                  >
                    <option value="">{of('placeholders.select')}</option>
                    {Object.entries(MOTOR_TYPES).map(([key, value]) => (
                      <option key={key} value={value}>{localizedMotorTypes[key]}</option>
                    ))}
                  </select>
                </div>

                {newOrder.motorType && (
                  <div className="form-group">
                    <label>{of('labels.motorFee', { currency: getMotorCurrency() })}</label>
                    <input
                      type="number"
                      step="0.01"
                      value={newOrder.motorPriceCurrency || ''}
                      onKeyDown={handleNumericKeyDown}
                      onChange={(e) => onFieldChange('motorPriceCurrency', e.target.value)}
                      disabled={isLoading}
                    />
                    {newOrder.motorPriceCurrency && (
                      <small style={{ display: 'block', marginTop: '5px', color: '#b04309' }}>
                        ≈ {newOrder.motorPrice ? Number(newOrder.motorPrice).toFixed(2) : 0} ₺
                      </small>
                    )}
                  </div>
                )}

                <div className="form-group">
                  <div className="label-with-icon">
                    <label>{of('labels.mounting')}</label>
                    <button
                      type="button"
                      className={`lock-btn ${showMountingCost ? 'active' : ''}`}
                      onClick={() => setShowMountingCost(!showMountingCost)}
                      title={of('titles.mountingCostEntry')}
                    >
                      <i className={`fas ${showMountingCost ? 'fa-lock-open' : 'fa-lock'}`}></i>
                    </button>
                  </div>

                  <input
                    type="number"
                    step="0.01"
                    value={newOrder.mounting}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('mounting', e.target.value)}
                    disabled={isLoading}
                  />
                </div>
                {showMountingCost && (
                  <div className="form-group">
                    <label className="extra-cost-label">{of('labels.mountingCost')}</label>
                    <input
                      type="number"
                      step="0.01"
                      value={newOrder.mountingCost || ''}
                      onKeyDown={handleNumericKeyDown}
                      onChange={(e) => onFieldChange('mountingCost', e.target.value)}
                      disabled={isLoading}
                      placeholder={of('placeholders.hiddenCost')}
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>{of('labels.quantityRequired')}</label>
                  <input
                    type="number"
                    step="1"
                    min="1"
                    value={newOrder.quantity || ''}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('quantity', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>{of('labels.discount')}</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={newOrder.discount}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('discount', e.target.value)}
                    disabled={isLoading}
                  />
                </div>

                {newOrder.totalPrice && (
                  <div className="form-group">
                    <label>{of('labels.totalAmount')}</label>
                    <input
                      type="text"
                      value={formatPrice(newOrder.totalPrice)}
                      readOnly
                      className="total-price"
                    />
                  </div>
                )}

                {newOrder.discountedPrice && parseFloat(newOrder.discount) > 0 && (
                  <div className="form-group">
                    <label>{of('labels.discountedAmount')}</label>
                    <input
                      type="text"
                      value={formatPrice(newOrder.discountedPrice)}
                      readOnly
                      className="discounted-price"
                    />
                  </div>
                )}
              </>
            )}

            {/* Plise için özel alanlar - Yeni Batch Sistemi */}
            {selectedProductType === 'plise' && (
              <>
                {/* Başlık Alanları */}
                {!isWholesale && (
                <div className="form-group">
                  <label>{of('labels.roomName')}</label>
                  <input
                    type="text"
                    value={newOrder.roomName}
                    onChange={(e) => onFieldChange('roomName', e.target.value)}
                    disabled={isLoading}
                    placeholder={of('placeholders.roomExample')}
                  />
                </div>
                )}

                <div className="form-group">
                  <label>{of('labels.product1Name')}</label>
                  <CreatableSelect
                    options={productOptions}
                    value={
                      productOptions.find(o => o.label === newOrder.pliseProduct1Name) ||
                      (newOrder.pliseProduct1Name ? { value: newOrder.pliseProduct1Name, label: newOrder.pliseProduct1Name } : null)
                    }
                    onChange={(option) => {
                      if (!option) {
                        onFieldChange('pliseProduct1Name', '');
                        onFieldChange('pliseProduct1Price', '');
                      } else if (option.__isNew__) {
                        onFieldChange('pliseProduct1Name', option.value);
                        onFieldChange('pliseProduct1Price', '');
                      } else {
                        handlePliseProduct1Select(option.value);
                      }
                    }}
                    isDisabled={isLoading}
                    isClearable
                    isSearchable
                    placeholder={of('placeholders.selectProduct')}
                    noOptionsMessage={() => of('messages.noProduct')}
                    formatCreateLabel={(inputValue) => of('placeholders.createFormat', { value: inputValue })}
                    styles={customSelectStyles}
                  />
                </div>

                <div className="form-group">
                  <label>{of('labels.product1Price')}</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={parseInt(newOrder.pliseProduct1Price) === 0 ? '' : newOrder.pliseProduct1Price}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('pliseProduct1Price', e.target.value)}
                    disabled={isLoading}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>{of('labels.product1FabricVariant')}</label>
                  <input
                    type="text"
                    value={newOrder.pliseProduct1Variation || ''}
                    onChange={(e) => onFieldChange('pliseProduct1Variation', e.target.value)}
                    disabled={isLoading}
                    placeholder={of('placeholders.productVariantShort')}
                  />
                </div>

                <div className="form-group">
                  <label>{of('labels.caseVariant')}</label>
                  <input
                    type="text"
                    value={newOrder.pliseCaseVariation || ''}
                    onChange={(e) => onFieldChange('pliseCaseVariation', e.target.value)}
                    disabled={isLoading}
                    placeholder={of('placeholders.colorExample')}
                  />
                </div>

                <div className="form-group">
                  <label>{of('labels.product2Name')}</label>
                  <CreatableSelect
                    options={productOptions}
                    value={
                      productOptions.find(o => o.label === newOrder.pliseProduct2Name) ||
                      (newOrder.pliseProduct2Name ? { value: newOrder.pliseProduct2Name, label: newOrder.pliseProduct2Name } : null)
                    }
                    onChange={(option) => {
                      if (!option) {
                        onFieldChange('pliseProduct2Name', '');
                        onFieldChange('pliseProduct2Price', '');
                      } else if (option.__isNew__) {
                        onFieldChange('pliseProduct2Name', option.value);
                        onFieldChange('pliseProduct2Price', '');
                      } else {
                        handlePliseProduct2Select(option.value);
                      }
                    }}
                    isDisabled={isLoading}
                    isClearable
                    isSearchable
                    placeholder={of('placeholders.selectProduct')}
                    noOptionsMessage={() => of('messages.noProduct')}
                    formatCreateLabel={(inputValue) => of('placeholders.createFormat', { value: inputValue })}
                    styles={customSelectStyles}
                  />
                </div>

                <div className="form-group">
                  <label>{of('labels.product2Price')}</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={parseInt(newOrder.pliseProduct2Price) === 0 ? '' : newOrder.pliseProduct2Price}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('pliseProduct2Price', e.target.value)}
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>{of('labels.product2FabricVariant')}</label>
                  <input
                    type="text"
                    value={newOrder.pliseProduct2Variation || ''}
                    onChange={(e) => onFieldChange('pliseProduct2Variation', e.target.value)}
                    disabled={isLoading}
                    placeholder={of('placeholders.productVariantShort')}
                  />
                </div>

                <div className="form-group">
                  <div className="label-with-icon">
                    <label>{of('labels.mountingTotal')}</label>
                    <button
                      type="button"
                      className={`lock-btn ${showMountingCost ? 'active' : ''}`}
                      onClick={() => setShowMountingCost(!showMountingCost)}
                      title={of('titles.mountingCostEntry')}
                    >
                      <i className={`fas ${showMountingCost ? 'fa-lock-open' : 'fa-lock'}`}></i>
                    </button>
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    value={newOrder.mounting}
                    onChange={(e) => onFieldChange('mounting', e.target.value)}
                    placeholder="0.00"
                    disabled={isLoading}
                  />
                </div>
                {showMountingCost && (
                  <div className="form-group">
                    <label className="extra-cost-label">{of('labels.mountingCost')}</label>
                    <input
                      type="number"
                      step="0.01"
                      value={newOrder.mountingCost || ''}
                      onKeyDown={handleNumericKeyDown}
                      onChange={(e) => onFieldChange('mountingCost', e.target.value)}
                      disabled={isLoading}
                      placeholder={of('placeholders.hiddenCost')}
                    />
                  </div>
                )}

                {/* Dinamik Ölçü Listesi Section */}
                <div style={{ gridColumn: '1 / -1', padding: '15px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', marginTop: '20px', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h4 style={{ margin: 0, fontSize: '16px', color: '#0f172a', fontWeight: '700', display: 'flex', alignItems: 'center' }}>
                      <i className="fas fa-layer-group" style={{ marginRight: '10px', color: '#b04309' }}></i>
                      {of('messages.pliseMeasureList')}
                    </h4>
                    <button
                      type="button"
                      onClick={() => {
                        const newList = [...(newOrder.pliseItems || []), { id: Date.now(), width: '', height: '', usedM2: '1.00', quantity: '1', multisistem: false, rowTotal: '0.00' }];
                        onFieldChange('pliseItems', newList);
                      }}
                      style={{ padding: '8px 16px', backgroundColor: '#0f172a', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '8px' }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#1e293b'}
                      onMouseOut={(e) => e.target.style.backgroundColor = '#0f172a'}
                    >
                      <i className="fas fa-plus"></i>
                      {of('messages.addNewMeasure')}
                    </button>
                  </div>

                  <div className="plise-batch-wrapper">
                    <table className="plise-batch-table">
                      <thead>
                        <tr>
                          <th>{of('labels.widthCmShort')}</th>
                          <th>{of('labels.heightCmShort')}</th>
                          <th>{of('labels.m2')}</th>
                          <th>{of('labels.quantity')}</th>
                          <th style={{ textAlign: 'center' }}>{of('labels.multisystem')}</th>
                          <th style={{ textAlign: 'right' }}>{of('labels.amount')}</th>
                          <th style={{ textAlign: 'center' }}>{of('labels.actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Array.isArray(newOrder.pliseItems) && newOrder.pliseItems.length > 0 ? (
                          newOrder.pliseItems.map((item, idx) => (
                            <tr key={item.id || idx} className="plise-row">
                              <td data-label={of('labels.widthCmShort')}>
                                <input
                                  type="number"
                                  value={item.width}
                                  onChange={(e) => {
                                    const newList = [...newOrder.pliseItems];
                                    newList[idx].width = e.target.value;
                                    onFieldChange('pliseItems', newList);
                                  }}
                                  className="plise-input"
                                  placeholder={of('placeholders.width')}
                                />
                              </td>
                              <td data-label={of('labels.heightCmShort')}>
                                <input
                                  type="number"
                                  value={item.height}
                                  onChange={(e) => {
                                    const newList = [...newOrder.pliseItems];
                                    newList[idx].height = e.target.value;
                                    onFieldChange('pliseItems', newList);
                                  }}
                                  className="plise-input"
                                  placeholder={of('placeholders.height')}
                                />
                              </td>
                              <td data-label={of('labels.m2')}>
                                <div className="plise-m2-badge">
                                  {item.usedM2 || '1.00'}
                                </div>
                              </td>
                              <td data-label={of('labels.quantity')}>
                                <input
                                  type="number"
                                  min="1"
                                  value={item.quantity}
                                  onChange={(e) => {
                                    const newList = [...newOrder.pliseItems];
                                    newList[idx].quantity = e.target.value;
                                    onFieldChange('pliseItems', newList);
                                  }}
                                  className="plise-input qty-input"
                                />
                              </td>
                              <td data-label={of('labels.multisystem')} style={{ textAlign: 'center' }}>
                                <input
                                  type="checkbox"
                                  checked={item.multisistem}
                                  onChange={(e) => {
                                    const newList = [...newOrder.pliseItems];
                                    newList[idx].multisistem = e.target.checked;
                                    onFieldChange('pliseItems', newList);
                                  }}
                                  className="plise-checkbox"
                                />
                              </td>
                              <td data-label="Tutar (₺)">
                                <div className="plise-row-total">
                                  {formatPrice(item.rowTotal || 0)} ₺
                                </div>
                              </td>
                              <td data-label="İşlemler">
                                <div className="plise-actions">
                                  <button
                                    type="button"
                                    className="plise-action-btn copy"
                                    title={of('titles.copyRow')}
                                    onClick={() => {
                                      const rowToCopy = { ...newOrder.pliseItems[idx], id: Date.now() + Math.random() };
                                      const newList = [...newOrder.pliseItems];
                                      newList.splice(idx + 1, 0, rowToCopy);
                                      onFieldChange('pliseItems', newList);
                                    }}
                                  >
                                    <i className="fas fa-copy"></i>
                                  </button>
                                  <button
                                    type="button"
                                    className="plise-action-btn delete"
                                    title={of('titles.deleteRow')}
                                    onClick={() => {
                                      const newList = newOrder.pliseItems.filter((_, i) => i !== idx);
                                      onFieldChange('pliseItems', newList);
                                    }}
                                  >
                                    <i className="fas fa-trash"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="7" className="empty-message">
                              {of('messages.noMeasureYet')}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="form-group">
                  <div className="label-with-icon">
                    <label>{of('labels.extraFee')}</label>
                    <button
                      type="button"
                      className={`lock-btn ${showExtraCost ? 'active' : ''}`}
                      onClick={() => setShowExtraCost(!showExtraCost)}
                      title={of('titles.extraCostEntry')}
                    >
                      <i className={`fas ${showExtraCost ? 'fa-lock-open' : 'fa-lock'}`}></i>
                    </button>
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newOrder.extraPrice}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => {
                      onFieldChange('extraPrice', e.target.value);
                      onFieldChange('manualExtraCost', '');
                    }}
                    disabled={isLoading}
                  />
                </div>

                {showExtraCost && (
                  <div className="form-group">
                    <label className="extra-cost-label">{of('labels.extraFeeCost')}</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newOrder.manualExtraCost || ''}
                      onKeyDown={handleNumericKeyDown}
                      onChange={(e) => onFieldChange('manualExtraCost', e.target.value)}
                      disabled={isLoading}
                      placeholder={of('placeholders.hiddenCost')}
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>{of('labels.discount')}</label>
                  <input
                    type="number"
                    value={newOrder.discount}
                    onChange={(e) => onFieldChange('discount', e.target.value)}
                    min="0"
                    max="100"
                    placeholder="0"
                    disabled={isLoading}
                  />
                </div>

                {newOrder.totalPrice && (
                  <div className="form-group">
                    <label>{of('labels.totalAmount')}</label>
                    <input
                      type="text"
                      value={formatPrice(newOrder.totalPrice)}
                      readOnly
                      className="total-price"
                    />
                  </div>
                )}

                {newOrder.discountedPrice && parseFloat(newOrder.discount) > 0 && (
                  <div className="form-group">
                    <label>{of('labels.discountedAmount')}</label>
                    <input
                      type="text"
                      value={formatPrice(newOrder.discountedPrice)}
                      readOnly
                      className="discounted-price"
                    />
                  </div>
                )}
              </>
            )}

            {/* Katlamalı Perde için özel alanlar */}
            {selectedProductType === 'katlamaliPerde' && (
              <>
                {!isWholesale && (
                <div className="form-group">
                  <label>{of('labels.roomName')}</label>
                  <input
                    type="text"
                    value={newOrder.roomName}
                    onChange={(e) => onFieldChange('roomName', e.target.value)}
                    disabled={isLoading}
                  />
                </div>
                )}

                <div className="form-group">
                  <label>{of('labels.productName')}</label>
                  <CreatableSelect
                    options={productOptions}
                    value={
                      productOptions.find(o => o.value === (productItems.find(p => p.name === newOrder.productName)?.id)) ||
                      (newOrder.productName ? { value: newOrder.productName, label: newOrder.productName } : null)
                    }
                    onChange={(option) => {
                      if (!option) {
                        onFieldChange('productName', '');
                        onFieldChange('unitPrice', '');
                      } else if (option.__isNew__) {
                        onFieldChange('productName', option.value);
                        onFieldChange('unitPrice', '');
                      } else {
                        handleProductSelect(option.value);
                      }
                    }}
                    isDisabled={isLoading}
                    isClearable
                    isSearchable
                    placeholder={of('placeholders.selectProduct')}
                    noOptionsMessage={() => of('messages.noProduct')}
                    formatCreateLabel={(inputValue) => of('placeholders.createFormat', { value: inputValue })}
                    styles={customSelectStyles}
                  />
                </div>

                <div className="form-group">
                  <label>{of('labels.productVariant')}</label>
                  <input
                    type="text"
                    value={newOrder.productVariant || ''}
                    onChange={(e) => onFieldChange('productVariant', e.target.value)}
                    disabled={isLoading}
                    placeholder={of('placeholders.productVariant')}
                  />
                </div>

                <div className="form-group">
                  <label>{of('labels.widthCm')}</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newOrder.width}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('width', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>{of('labels.heightCm')}</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newOrder.height}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('height', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                {newOrder.usedM2 && (
                  <div className="form-group">
                    <label>{of('labels.usedMt')}</label>
                    <input
                      type="text"
                      value={newOrder.usedM2}
                      readOnly
                      className="readonly-field"
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>{of('labels.fabricPrice')}</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={newOrder.fabricPrice}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('fabricPrice', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>{of('labels.mechanismType')}</label>
                  {adminSettings.katlamaliMekanizmaList && adminSettings.katlamaliMekanizmaList.length > 0 ? (
                    <Select
                      options={adminSettings.katlamaliMekanizmaList.map(m => {
                        const converted = convertMechanismToKztForOrder(m, exchangeRates);
                        const wPrice = (converted.wholesale !== undefined && converted.wholesale !== null && converted.wholesale !== 0) ? converted.wholesale : converted.price;
                        const dispPrice = isWholesale ? wPrice : converted.price;
                        return { value: m.type, label: `${m.type} (${formatPrice(dispPrice)} ${CURRENCY_SYMBOL})` };
                      })}
                      value={newOrder.mechanismType ? { value: newOrder.mechanismType, label: newOrder.mechanismType } : null}
                      onChange={(option) => handleKatlamaliMechanismSelect(option ? option.value : '')}
                      isDisabled={isLoading}
                      isClearable
                      isSearchable
                      placeholder={of('placeholders.selectOption')}
                      styles={customSelectStyles}
                    />
                  ) : (
                    <input
                      type="text"
                      value={newOrder.mechanismType || ''}
                      onChange={(e) => onFieldChange('mechanismType', e.target.value)}
                      disabled={isLoading}
                      placeholder={of('placeholders.selectMechanism')}
                    />
                  )}
                </div>

                <div className="form-group">
                  <label>{of('labels.mechanismPrice')}</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={newOrder.mechanismPrice}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('mechanismPrice', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                {/* Aksesuarlar Bölümü */}
                <div style={{ gridColumn: '1 / -1', padding: '15px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', marginTop: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <h4 style={{ margin: 0, fontSize: '15px', color: '#b04309' }}>
                      <i className="fas fa-tags" style={{ marginRight: '8px' }}></i>
                      {of('sections.accessories')}
                    </h4>
                    <button
                      type="button"
                      onClick={handleAddAccessory}
                      style={{ padding: '6px 12px', backgroundColor: '#0f172a', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}
                    >
                      <i className="fas fa-plus" style={{ marginRight: '5px' }}></i>
                      {of('sections.addAccessory')}
                    </button>
                  </div>

                  {Array.isArray(newOrder.accessories) && newOrder.accessories.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {newOrder.accessories.map((acc, idx) => (
                        <div key={acc.id} className="aksesuar-item-row">
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label style={{ fontSize: '11px' }}>{of('labels.accessoryName')}</label>
                            <input
                              type="text"
                              value={acc.name}
                              onChange={(e) => handleAccessoryChange(acc.id, 'name', e.target.value)}
                              placeholder={of('placeholders.accessoryExample')}
                              style={{ padding: '6px' }}
                            />
                          </div>
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label style={{ fontSize: '11px' }}>Fiyat (₺)</label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={acc.price}
                              onKeyDown={handleNumericKeyDown}
                              onChange={(e) => handleAccessoryChange(acc.id, 'price', e.target.value)}
                              style={{ padding: '6px' }}
                            />
                          </div>
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label style={{ fontSize: '11px' }}>Adet</label>
                            <input
                              type="number"
                              step="1"
                              min="1"
                              value={acc.quantity}
                              onKeyDown={handleNumericKeyDown}
                              onChange={(e) => handleAccessoryChange(acc.id, 'quantity', e.target.value)}
                              style={{ padding: '6px' }}
                            />
                          </div>
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label style={{ fontSize: '11px' }}>Tutar (₺)</label>
                            <input
                              type="text"
                              value={acc.total}
                              readOnly
                              style={{ padding: '6px', backgroundColor: '#f1f5f9' }}
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveAccessory(acc.id)}
                            style={{ padding: '8px', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ fontSize: '13px', color: '#64748b', textAlign: 'center', margin: '10px 0' }}>{of('messages.noAccessoryYet')}</p>
                  )}
                </div>

                {/* Motor Bölümü */}
                <div className="form-group">
                  <label>{of('labels.motor')}</label>
                  <select
                    value={newOrder.motorType || ''}
                    onChange={(e) => onFieldChange('motorType', e.target.value)}
                    disabled={isLoading}
                  >
                    <option value="">{of('placeholders.select')}</option>
                    {Object.entries(MOTOR_TYPES).map(([key, value]) => (
                      <option key={key} value={value}>{localizedMotorTypes[key]}</option>
                    ))}
                  </select>
                </div>

                {newOrder.motorType && (
                  <div className="form-group">
                    <label>{of('labels.motorFee', { currency: getMotorCurrency() })}</label>
                    <input
                      type="number"
                      step="0.01"
                      value={newOrder.motorPriceCurrency || ''}
                      onKeyDown={handleNumericKeyDown}
                      onChange={(e) => onFieldChange('motorPriceCurrency', e.target.value)}
                      disabled={isLoading}
                    />
                    {newOrder.motorPriceCurrency && (
                      <small style={{ display: 'block', marginTop: '5px', color: '#b04309' }}>
                        ≈ {newOrder.motorPrice ? Number(newOrder.motorPrice).toFixed(2) : 0} ₺
                      </small>
                    )}
                  </div>
                )}

                <div className="form-group">
                  <div className="label-with-icon">
                    <label>{of('labels.mounting')}</label>
                    <button
                      type="button"
                      className={`lock-btn ${showMountingCost ? 'active' : ''}`}
                      onClick={() => setShowMountingCost(!showMountingCost)}
                      title={of('titles.mountingCostEntry')}
                    >
                      <i className={`fas ${showMountingCost ? 'fa-lock-open' : 'fa-lock'}`}></i>
                    </button>
                  </div>

                  <input
                    type="number"
                    step="0.01"
                    value={newOrder.mounting}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('mounting', e.target.value)}
                    disabled={isLoading}
                  />
                </div>
                {showMountingCost && (
                  <div className="form-group">
                    <label className="extra-cost-label">{of('labels.mountingCost')}</label>
                    <input
                      type="number"
                      step="0.01"
                      value={newOrder.mountingCost || ''}
                      onKeyDown={handleNumericKeyDown}
                      onChange={(e) => onFieldChange('mountingCost', e.target.value)}
                      disabled={isLoading}
                      placeholder={of('placeholders.hiddenCost')}
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>{of('labels.quantityRequired')}</label>
                  <input
                    type="number"
                    step="1"
                    min="1"
                    value={newOrder.quantity || ''}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('quantity', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>{of('labels.discount')}</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={newOrder.discount}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('discount', e.target.value)}
                    disabled={isLoading}
                  />
                </div>

                {newOrder.totalPrice && (
                  <div className="form-group">
                    <label>{of('labels.totalAmount')}</label>
                    <input
                      type="text"
                      value={formatPrice(newOrder.totalPrice)}
                      readOnly
                      className="total-price"
                    />
                  </div>
                )}

                {newOrder.discountedPrice && parseFloat(newOrder.discount) > 0 && (
                  <div className="form-group">
                    <label>{of('labels.discountedAmount')}</label>
                    <input
                      type="text"
                      value={formatPrice(newOrder.discountedPrice)}
                      readOnly
                      className="discounted-price"
                    />
                  </div>
                )}
              </>
            )}

            {/* Aksesuar için özel alanlar */}
            {selectedProductType === 'aksesuar' && (
              <>
                {!isWholesale && (
                <div className="form-group">
                  <label>{of('labels.roomName')}</label>
                  <input
                    type="text"
                    value={newOrder.roomName}
                    onChange={(e) => onFieldChange('roomName', e.target.value)}
                    disabled={isLoading}
                  />
                </div>
                )}

                <div className="form-group">
                  <label>{of('labels.productName')}</label>
                  <CreatableSelect
                    options={productOptions}
                    value={
                      productOptions.find(o => o.value === (productItems.find(p => p.name === newOrder.productName)?.id)) ||
                      (newOrder.productName ? { value: newOrder.productName, label: newOrder.productName } : null)
                    }
                    onChange={(option) => {
                      if (!option) {
                        onFieldChange('productName', '');
                        onFieldChange('unitPrice', '');
                      } else if (option.__isNew__) {
                        onFieldChange('productName', option.value);
                        onFieldChange('unitPrice', '');
                      } else {
                        handleProductSelect(option.value);
                      }
                    }}
                    isDisabled={isLoading}
                    isClearable
                    isSearchable
                    placeholder={of('placeholders.selectProduct')}
                    noOptionsMessage={() => of('messages.noProduct')}
                    formatCreateLabel={(inputValue) => of('placeholders.createFormat', { value: inputValue })}
                    styles={customSelectStyles}
                  />
                </div>

                <div className="form-group">
                  <label>{of('labels.productVariant')}</label>
                  <input
                    type="text"
                    value={newOrder.productVariant || ''}
                    onChange={(e) => onFieldChange('productVariant', e.target.value)}
                    disabled={isLoading}
                    placeholder={of('placeholders.productVariant')}
                  />
                </div>

                <div className="form-group">
                  <label>{of('labels.unitType')}</label>
                  <select
                    className="form-control"
                    value={newOrder.unitType || 'Adet'}
                    onChange={(e) => onFieldChange('unitType', e.target.value)}
                    disabled={isLoading}
                  >
                    <option value="Adet">{of('unitTypes.piece')}</option>
                    <option value="Metre">{of('unitTypes.meter')}</option>
                  </select>
                </div>

                {newOrder.unitType === 'Metre' && (
                  <div className="form-group">
                    <label>{of('labels.meters')}</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={newOrder.itemMeter}
                      onKeyDown={handleNumericKeyDown}
                      onChange={(e) => onFieldChange('itemMeter', e.target.value)}
                      required
                      disabled={isLoading}
                      placeholder={of('placeholders.howManyMeters')}
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>{of('labels.quantityRequired')}</label>
                  <input
                    type="number"
                    step="1"
                    min="1"
                    value={newOrder.quantity}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('quantity', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <div className="label-with-icon">
                    <label>{of('labels.unitPrice')}</label>
                    <button
                      type="button"
                      className={`lock-btn ${showExtraCost ? 'active' : ''}`}
                      onClick={() => setShowExtraCost(!showExtraCost)}
                      title={of('titles.costEntry')}
                    >
                      <i className={`fas ${showExtraCost ? 'fa-lock-open' : 'fa-lock'}`}></i>
                    </button>
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newOrder.unitPrice}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('unitPrice', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                {showExtraCost && (
                  <div className="form-group">
                    <label className="extra-cost-label">{of('labels.unitCostAlt')}</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newOrder.manualExtraCost || ''}
                      onKeyDown={handleNumericKeyDown}
                      onChange={(e) => onFieldChange('manualExtraCost', e.target.value)}
                      disabled={isLoading}
                      placeholder={of('placeholders.hiddenCost')}
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>{of('labels.discount')}</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={newOrder.discount}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('discount', e.target.value)}
                    disabled={isLoading}
                  />
                </div>

                {newOrder.totalPrice && (
                  <div className="form-group">
                    <label>{of('labels.totalAmount')}</label>
                    <input
                      type="text"
                      value={formatPrice(newOrder.totalPrice)}
                      readOnly
                      className="total-price"
                    />
                  </div>
                )}

                {newOrder.discountedPrice && parseFloat(newOrder.discount) > 0 && (
                  <div className="form-group">
                    <label>{of('labels.discountedAmount')}</label>
                    <input
                      type="text"
                      value={formatPrice(newOrder.discountedPrice)}
                      readOnly
                      className="discounted-price"
                    />
                  </div>
                )}
              </>
            )}

            {/* Ray Sistemleri için özel alanlar */}
            {selectedProductType === 'raySistemi' && (
              <>
                {!isWholesale && (
                <div className="form-group">
                  <label>{of('labels.roomName')}</label>
                  <input
                    type="text"
                    value={newOrder.roomName}
                    onChange={(e) => onFieldChange('roomName', e.target.value)}
                    disabled={isLoading}
                  />
                </div>
                )}

                <div className="form-group">
                  <label>{of('labels.productName')}</label>
                  <CreatableSelect
                    options={productOptions}
                    value={
                      productOptions.find(o => o.value === (productItems.find(p => p.name === newOrder.productName)?.id)) ||
                      (newOrder.productName ? { value: newOrder.productName, label: newOrder.productName } : null)
                    }
                    onChange={(option) => {
                      if (!option) {
                        onFieldChange('productName', '');
                        onFieldChange('unitPrice', '');
                      } else if (option.__isNew__) {
                        onFieldChange('productName', option.value);
                        onFieldChange('unitPrice', '');
                      } else {
                        handleProductSelect(option.value);
                      }
                    }}
                    isDisabled={isLoading}
                    isClearable
                    isSearchable
                    placeholder={of('placeholders.selectProduct')}
                    noOptionsMessage={() => of('messages.noProduct')}
                    formatCreateLabel={(inputValue) => of('placeholders.createFormat', { value: inputValue })}
                    styles={customSelectStyles}
                  />
                </div>

                <div className="form-group">
                  <label>{of('labels.productVariant')}</label>
                  <input
                    type="text"
                    value={newOrder.productVariant || ''}
                    onChange={(e) => onFieldChange('productVariant', e.target.value)}
                    disabled={isLoading}
                    placeholder={of('placeholders.productVariant')}
                  />
                </div>

                <div className="form-group">
                  <label>{of('labels.widthCm')}</label>
                  <input
                    type="number"
                    step="1"
                    value={newOrder.width}
                    onChange={(e) => onFieldChange('width', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>{of('labels.measureCompletion')}</label>
                  <select
                    value={newOrder.olcuTamamlama || ''}
                    onChange={(e) => onFieldChange('olcuTamamlama', e.target.value)}
                    required
                    disabled={isLoading}
                  >
                    <option value="">{of('placeholders.select')}</option>
                    <option value="25cm">{of('measureCompletion.complete25')}</option>
                    <option value="50cm">{of('measureCompletion.complete50')}</option>
                    <option value="urunBoyutu">{of('measureCompletion.productSize')}</option>
                  </select>
                </div>

                {newOrder.usedMeter && (
                  <div className="form-group">
                    <label>{of('labels.usedMeter')}</label>
                    <input
                      type="text"
                      value={newOrder.usedMeter + ' m'}
                      readOnly
                      className="readonly-field"
                    />
                  </div>
                )}

                <div className="form-group">
                  <div className="label-with-icon">
                    <label>{of('labels.unitPrice')}</label>
                    <button
                      type="button"
                      className={`lock-btn ${showBaseCost ? 'active' : ''}`}
                      onClick={() => setShowBaseCost(!showBaseCost)}
                      title={of('titles.costInfo')}
                    >
                      <i className={`fas ${showBaseCost ? 'fa-lock-open' : 'fa-lock'}`}></i>
                    </button>
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={newOrder.unitPrice}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => {
                      onFieldChange('unitPrice', e.target.value);
                      onFieldChange('baseUnitCost', '');
                    }}
                    required
                    disabled={isLoading}
                  />
                </div>

                {showBaseCost && (
                  <div className="form-group">
                    <label className="extra-cost-label">{of('labels.unitCost')}</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newOrder.baseUnitCost || ''}
                      onKeyDown={handleNumericKeyDown}
                      onChange={(e) => onFieldChange('baseUnitCost', e.target.value)}
                      disabled={isLoading}
                      placeholder={of('placeholders.hiddenCost')}
                    />
                  </div>
                )}


                <div className="form-group">
                  <div className="label-with-icon">
                    <label>{of('labels.mounting')}</label>
                    <button
                      type="button"
                      className={`lock-btn ${showMountingCost ? 'active' : ''}`}
                      onClick={() => setShowMountingCost(!showMountingCost)}
                      title={of('titles.mountingCostEntry')}
                    >
                      <i className={`fas ${showMountingCost ? 'fa-lock-open' : 'fa-lock'}`}></i>
                    </button>
                  </div>

                  <input
                    type="number"
                    step="0.01"
                    value={newOrder.mounting || ''}
                    onChange={(e) => onFieldChange('mounting', e.target.value)}
                    disabled={isLoading}
                  />
                </div>
                {showMountingCost && (
                  <div className="form-group">
                    <label className="extra-cost-label">{of('labels.mountingCost')}</label>
                    <input
                      type="number"
                      step="0.01"
                      value={newOrder.mountingCost || ''}
                      onChange={(e) => onFieldChange('mountingCost', e.target.value)}
                      onKeyDown={handleNumericKeyDown}
                      disabled={isLoading}
                      placeholder={of('placeholders.hiddenCost')}
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>{of('labels.quantityRequired')}</label>
                  <input
                    type="number"
                    step="1"
                    min="1"
                    value={newOrder.quantity || ''}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('quantity', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>{of('labels.discount')}</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={newOrder.discount}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => onFieldChange('discount', e.target.value)}
                    disabled={isLoading}
                  />
                </div>

                {newOrder.totalPrice && (
                  <div className="form-group">
                    <label>{of('labels.totalAmount')}</label>
                    <input
                      type="text"
                      value={formatPrice(newOrder.totalPrice)}
                      readOnly
                      className="total-price"
                    />
                  </div>
                )}

                {newOrder.discountedPrice && parseFloat(newOrder.discount) > 0 && (
                  <div className="form-group">
                    <label>{of('labels.discountedAmount')}</label>
                    <input
                      type="text"
                      value={formatPrice(newOrder.discountedPrice)}
                      readOnly
                      className="discounted-price"
                    />
                  </div>
                )}
              </>
            )}

            {/* Rustik için özel alanlar */}
            {selectedProductType === 'rustik' && (
              <>
                {!isWholesale && (
                <div className="form-group">
                  <label>{of('labels.roomName')}</label>
                  <input
                    type="text"
                    value={newOrder.roomName}
                    onChange={(e) => onFieldChange('roomName', e.target.value)}
                    disabled={isLoading}
                    placeholder={of('placeholders.roomExample')}
                  />
                </div>
                )}

                <div className="form-group">
                  <label>{of('labels.productName')}</label>
                  <CreatableSelect
                    options={productOptions}
                    value={
                      productOptions.find(o => o.label === newOrder.productName) ||
                      (newOrder.productName ? { value: newOrder.productName, label: newOrder.productName } : null)
                    }
                    onChange={(option) => {
                      if (!option) {
                        onFieldChange('productName', '');
                        onFieldChange('unitPrice', '');
                      } else if (option.__isNew__) {
                        onFieldChange('productName', option.value);
                        onFieldChange('unitPrice', '');
                      } else {
                        handleProductSelect(option.value);
                      }
                    }}
                    isDisabled={isLoading}
                    isClearable
                    isSearchable
                    placeholder={of('placeholders.selectProduct')}
                    noOptionsMessage={() => of('messages.noProduct')}
                    formatCreateLabel={(inputValue) => of('placeholders.createFormat', { value: inputValue })}
                    styles={customSelectStyles}
                  />
                </div>
                <div className="form-group">
                  <label>{of('labels.productVariant')}</label>
                  <input
                    type="text"
                    value={newOrder.productVariant || ''}
                    onChange={(e) => onFieldChange('productVariant', e.target.value)}
                    disabled={isLoading}
                    placeholder={of('placeholders.productVariant')}
                  />
                </div>

                <div className="form-group">
                  <label>{of('labels.widthCm')}</label>
                  <input
                    type="number"
                    value={newOrder.width}
                    onChange={(e) => onFieldChange('width', e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>{of('labels.usedCm')}</label>
                  <input
                    type="number"
                    value={newOrder.usedCm}
                    onChange={(e) => onFieldChange('usedCm', e.target.value)}
                    disabled={isLoading}
                    className="readonly-field"
                  />
                </div>

                <div className="form-group">
                  <div className="label-with-icon">
                    <label>{of('labels.unitPrice')}</label>
                    <button
                      type="button"
                      className={`lock-btn ${showBaseCost ? 'active' : ''}`}
                      onClick={() => setShowBaseCost(!showBaseCost)}
                      title={of('titles.costInfo')}
                    >
                      <i className={`fas ${showBaseCost ? 'fa-lock-open' : 'fa-lock'}`}></i>
                    </button>
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={newOrder.unitPrice}
                    onKeyDown={handleNumericKeyDown}
                    onChange={(e) => {
                      onFieldChange('unitPrice', e.target.value);
                      onFieldChange('baseUnitCost', '');
                    }}
                    required
                    disabled={isLoading}
                  />
                </div>

                {showBaseCost && (
                  <div className="form-group">
                    <label className="extra-cost-label">{of('labels.unitCost')}</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newOrder.baseUnitCost || ''}
                      onKeyDown={handleNumericKeyDown}
                      onChange={(e) => onFieldChange('baseUnitCost', e.target.value)}
                      disabled={isLoading}
                      placeholder={of('placeholders.hiddenCost')}
                    />
                  </div>
                )}


                {/* Ayak Ayarları */}
                <div className="form-group">
                  <label>{of('labels.legModel')}</label>
                  {adminSettings.rustikAyakModelList?.length > 0 ? (
                    <Select
                      options={adminSettings.rustikAyakModelList.map(m => ({ value: m, label: m }))}
                      value={newOrder.ayakModel ? { value: newOrder.ayakModel, label: newOrder.ayakModel } : null}
                      onChange={(option) => onFieldChange('ayakModel', option ? option.value : '')}
                      styles={customSelectStyles}
                      isClearable
                    />
                  ) : (
                    <input type="text" value={newOrder.ayakModel} onChange={(e) => onFieldChange('ayakModel', e.target.value)} required />
                  )}
                </div>

                <div className="form-group">
                  <label>{of('labels.legDiameter')}</label>
                  {adminSettings.rustikAyakCapList?.length > 0 ? (
                    <Select
                      options={adminSettings.rustikAyakCapList.map(m => ({ value: m, label: m }))}
                      value={newOrder.ayakCap ? { value: newOrder.ayakCap, label: newOrder.ayakCap } : null}
                      onChange={(option) => onFieldChange('ayakCap', option ? option.value : '')}
                      styles={customSelectStyles}
                      isClearable
                    />
                  ) : (
                    <input type="text" value={newOrder.ayakCap} onChange={(e) => onFieldChange('ayakCap', e.target.value)} required />
                  )}
                </div>

                <div className="form-group">
                  <label>{of('labels.legLength')}</label>
                  {adminSettings.rustikAyakUzunlukList?.length > 0 ? (
                    <Select
                      options={adminSettings.rustikAyakUzunlukList.map(m => ({ value: m, label: m }))}
                      value={newOrder.ayakUzunluk ? { value: newOrder.ayakUzunluk, label: newOrder.ayakUzunluk } : null}
                      onChange={(option) => onFieldChange('ayakUzunluk', option ? option.value : '')}
                      styles={customSelectStyles}
                      isClearable
                    />
                  ) : (
                    <input type="text" value={newOrder.ayakUzunluk} onChange={(e) => onFieldChange('ayakUzunluk', e.target.value)} required />
                  )}
                </div>

                <div className="form-group">
                  <label>{of('labels.colorCode')}</label>
                  {adminSettings.rustikAyakRenkKoduList?.length > 0 ? (
                    <Select
                      options={adminSettings.rustikAyakRenkKoduList.map(m => ({ value: m, label: m }))}
                      value={newOrder.ayakRenkKodu ? { value: newOrder.ayakRenkKodu, label: newOrder.ayakRenkKodu } : null}
                      onChange={(option) => onFieldChange('ayakRenkKodu', option ? option.value : '')}
                      styles={customSelectStyles}
                      isClearable
                    />
                  ) : (
                    <input type="text" value={newOrder.ayakRenkKodu} onChange={(e) => onFieldChange('ayakRenkKodu', e.target.value)} required />
                  )}
                </div>

                <div className="form-group">
                  <label>{of('labels.legCount')}</label>
                  <Select
                    options={[1, 2, 3, 4, 5].map(v => ({ value: v, label: v }))}
                    value={newOrder.ayakSayisi ? { value: newOrder.ayakSayisi, label: newOrder.ayakSayisi } : null}
                    onChange={(option) => onFieldChange('ayakSayisi', option ? option.value : '')}
                    styles={customSelectStyles}
                  />
                </div>

                <div className="form-group">
                  <label>{of('labels.extraLegCount')}</label>
                  <Select
                    options={[1, 2, 3, 4, 5].map(v => ({ value: v, label: v }))}
                    value={newOrder.ekAyakSayisi ? { value: newOrder.ekAyakSayisi, label: newOrder.ekAyakSayisi } : null}
                    onChange={(option) => onFieldChange('ekAyakSayisi', option ? option.value : '')}
                    styles={customSelectStyles}
                    isClearable
                  />
                </div>

                <div className="form-group">
                  <label>{of('labels.legFee')}</label>
                  <input type="number" step="0.01" min="0" onKeyDown={handleNumericKeyDown} value={newOrder.ayakUcreti} onChange={(e) => onFieldChange('ayakUcreti', e.target.value)} />
                </div>

                <div className="form-group">
                  <div className="label-with-icon">
                    <label>{of('labels.mounting')}</label>
                    <button
                      type="button"
                      className={`lock-btn ${showMountingCost ? 'active' : ''}`}
                      onClick={() => setShowMountingCost(!showMountingCost)}
                      title={of('titles.mountingCostEntry')}
                    >
                      <i className={`fas ${showMountingCost ? 'fa-lock-open' : 'fa-lock'}`}></i>
                    </button>
                  </div>

                  <input type="number" step="0.01" value={newOrder.mounting} onChange={(e) => onFieldChange('mounting', e.target.value)} />
                </div>
                {showMountingCost && (
                  <div className="form-group">
                    <label className="extra-cost-label">{of('labels.mountingCost')}</label>
                    <input type="number" step="0.01" value={newOrder.mountingCost || ''} onKeyDown={handleNumericKeyDown} onChange={(e) => onFieldChange('mountingCost', e.target.value)} disabled={isLoading} placeholder={of('placeholders.hiddenCost')} />
                  </div>
                )}

                {/* Ray Seçenekleri ve Aksesuarlar */}
                <div style={{ gridColumn: '1 / -1', padding: '15px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', marginTop: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <h4 style={{ margin: 0, fontSize: '15px', color: '#b04309' }}>
                      <i className="fas fa-road" style={{ marginRight: '8px' }}></i>
                      {of('sections.rayOptions')}
                    </h4>
                    <button
                      type="button"
                      onClick={() => {
                        const newList = [...(newOrder.rustikRaySecenekleri || []), { name: '', price: '0', quantity: newOrder.width || '1' }];
                        onFieldChange('rustikRaySecenekleri', newList);
                      }}
                      style={{ padding: '6px 12px', backgroundColor: '#0f172a', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}
                    >
                      <i className="fas fa-plus" style={{ marginRight: '5px' }}></i>
                      {of('sections.addOption')}
                    </button>
                  </div>

                  {Array.isArray(newOrder.rustikRaySecenekleri) && newOrder.rustikRaySecenekleri.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
                      {newOrder.rustikRaySecenekleri.map((ray, idx) => (
                        <div key={idx} className="rustik-item-row">
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label style={{ fontSize: '11px' }}>{of('labels.optionName')}</label>
                            <input
                              type="text"
                              value={ray.name}
                              onChange={(e) => {
                                const newList = [...newOrder.rustikRaySecenekleri];
                                newList[idx].name = e.target.value;
                                onFieldChange('rustikRaySecenekleri', newList);
                              }}
                              placeholder={of('placeholders.stormRail')}
                              style={{ padding: '6px' }}
                            />
                          </div>
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label style={{ fontSize: '11px' }}>Fiyat (₺)</label>
                            <input
                              type="number"
                              min="0"
                              onKeyDown={handleNumericKeyDown}
                              value={ray.price}
                              onChange={(e) => {
                                const newList = [...newOrder.rustikRaySecenekleri];
                                newList[idx].price = e.target.value;
                                onFieldChange('rustikRaySecenekleri', newList);
                              }}
                              style={{ padding: '6px' }}
                            />
                          </div>
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label style={{ fontSize: '11px' }}>En (cm)</label>
                            <input
                              type="number"
                              min="1"
                              onKeyDown={handleNumericKeyDown}
                              value={ray.quantity || newOrder.width || ''}
                              onChange={(e) => {
                                const newList = [...newOrder.rustikRaySecenekleri];
                                newList[idx].quantity = e.target.value;
                                onFieldChange('rustikRaySecenekleri', newList);
                              }}
                              style={{ padding: '6px' }}
                            />
                          </div>
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label style={{ fontSize: '11px' }}>Tutar (₺)</label>
                            <input
                              type="text"
                              value={formatPrice(parseFloat(ray.price) * (parseFloat(newOrder.usedCm) / 100) || 0)}
                              readOnly
                              style={{ padding: '6px', backgroundColor: '#f1f5f9' }}
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const newList = newOrder.rustikRaySecenekleri.filter((_, i) => i !== idx);
                              onFieldChange('rustikRaySecenekleri', newList);
                            }}
                            style={{ padding: '8px', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ fontSize: '13px', color: '#64748b', textAlign: 'center', margin: '10px 0 20px 0' }}>{of('messages.noRayOptionYet')}</p>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', borderTop: '1px solid #e2e8f0', paddingTop: '15px' }}>
                    <h4 style={{ margin: 0, fontSize: '15px', color: '#b04309' }}>
                      <i className="fas fa-tags" style={{ marginRight: '8px' }}></i>
                      {of('sections.accessories')}
                    </h4>
                    <button
                      type="button"
                      onClick={() => {
                        const newList = [...(newOrder.rustikAksesuarlar || []), { name: '', price: '0', quantity: '1' }];
                        onFieldChange('rustikAksesuarlar', newList);
                      }}
                      style={{ padding: '6px 12px', backgroundColor: '#0f172a', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}
                    >
                      <i className="fas fa-plus" style={{ marginRight: '5px' }}></i>
                      {of('sections.addAccessory')}
                    </button>
                  </div>

                  {Array.isArray(newOrder.rustikAksesuarlar) && newOrder.rustikAksesuarlar.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {newOrder.rustikAksesuarlar.map((acc, idx) => (
                        <div key={idx} className="rustik-item-row">
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label style={{ fontSize: '11px' }}>{of('labels.accessoryName')}</label>
                            <input
                              type="text"
                              value={acc.name}
                              onChange={(e) => {
                                const newList = [...newOrder.rustikAksesuarlar];
                                newList[idx].name = e.target.value;
                                onFieldChange('rustikAksesuarlar', newList);
                              }}
                              placeholder={of('placeholders.accessoryExample')}
                              style={{ padding: '6px' }}
                            />
                          </div>
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label style={{ fontSize: '11px' }}>Fiyat (₺)</label>
                            <input
                              type="number"
                              min="0"
                              onKeyDown={handleNumericKeyDown}
                              value={acc.price}
                              onChange={(e) => {
                                const newList = [...newOrder.rustikAksesuarlar];
                                newList[idx].price = e.target.value;
                                onFieldChange('rustikAksesuarlar', newList);
                              }}
                              style={{ padding: '6px' }}
                            />
                          </div>
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label style={{ fontSize: '11px' }}>Adet</label>
                            <input
                              type="number"
                              min="1"
                              onKeyDown={handleNumericKeyDown}
                              value={acc.quantity}
                              onChange={(e) => {
                                const newList = [...newOrder.rustikAksesuarlar];
                                newList[idx].quantity = e.target.value;
                                onFieldChange('rustikAksesuarlar', newList);
                              }}
                              style={{ padding: '6px' }}
                            />
                          </div>
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label style={{ fontSize: '11px' }}>Tutar (₺)</label>
                            <input
                              type="text"
                              value={formatPrice(parseFloat(acc.price) * parseInt(acc.quantity) || 0)}
                              readOnly
                              style={{ padding: '6px', backgroundColor: '#f1f5f9' }}
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const newList = newOrder.rustikAksesuarlar.filter((_, i) => i !== idx);
                              onFieldChange('rustikAksesuarlar', newList);
                            }}
                            style={{ padding: '8px', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ fontSize: '13px', color: '#64748b', textAlign: 'center', margin: '10px 0' }}>{of('messages.noAccessoryYet')}</p>
                  )}
                </div>

                <div className="form-group">
                  <label>{of('labels.quantityRequired')}</label>
                  <input
                    type="number"
                    value={newOrder.quantity}
                    onChange={(e) => onFieldChange('quantity', e.target.value)}
                    min="1"
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label>{of('labels.discount')}</label>
                  <input
                    type="number"
                    value={newOrder.discount}
                    onChange={(e) => onFieldChange('discount', e.target.value)}
                    min="0"
                    max="100"
                    disabled={isLoading}
                  />
                </div>

                {newOrder.totalPrice && (
                  <div className="form-group">
                    <label>{of('labels.totalAmount')}</label>
                    <input
                      type="text"
                      value={formatPrice(newOrder.totalPrice)}
                      readOnly
                      className="total-price"
                    />
                  </div>
                )}

                {newOrder.discountedPrice && parseFloat(newOrder.discount) > 0 && (
                  <div className="form-group">
                    <label>{of('labels.discountedAmount')}</label>
                    <input
                      type="text"
                      value={formatPrice(newOrder.discountedPrice)}
                      readOnly
                      className="discounted-price"
                    />
                  </div>
                )}
              </>
            )}

            <div className="form-group">
              <label>{of('labels.notes')}</label>
              <textarea
                value={newOrder.notes}
                onChange={(e) => onFieldChange('notes', e.target.value)}
                rows="2"
                disabled={isLoading}
              />
            </div>
          </div>
        )
        }

        <div className="form-actions">
          <div className="action-buttons">
            <button
              type="submit"
              className="save-btn"
              disabled={isLoading || !selectedProductType}
            >
              {isLoading ? t('common.saving') : t('common.save')}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="cancel-btn"
              disabled={isLoading}
            >
              {t('common.cancel')}
            </button>
          </div>

          {/* WhatsApp Gönderme Bölümü - Sağ tarafta */}
          {selectedProductType && (newOrder.productName || (selectedProductType === 'plise' && newOrder.pliseProduct1Name)) && newOrder.roomName && (
            <div className="whatsapp-section-right">
              {selectedProductType === 'katlamaliPerde' ? (
                /* Katlamalı Perde için Özel 4'lü Firma Seçimi */
                <div className="katlamali-whatsapp-grid">
                  {[
                    { id: 'kumas', label: of('whatsapp.fabricCompany'), value: selectedFirmaKumas, setter: setSelectedFirmaKumas, lastSent: lastSentFirmaKumas },
                    { id: 'mekanizma', label: of('whatsapp.mechanismCompany'), value: selectedFirmaMekanizma, setter: setSelectedFirmaMekanizma, lastSent: lastSentFirmaMekanizma },
                    { id: 'motor', label: of('whatsapp.motorCompany'), value: selectedFirmaMotor, setter: setSelectedFirmaMotor, lastSent: lastSentFirmaMotor },
                    { id: 'aksesuar', label: of('whatsapp.accessoryCompany'), value: selectedFirmaAksesuar, setter: setSelectedFirmaAksesuar, lastSent: lastSentFirmaAksesuar }
                  ].map((item) => (
                    <div key={item.id} className="whatsapp-control-row">
                      <div className="whatsapp-row-header">
                        <span className="row-label">{item.label}</span>
                        {item.lastSent && (
                          <span className="row-sent-info">
                            <i className="fas fa-check"></i> {item.lastSent.name}
                          </span>
                        )}
                      </div>
                      <div className="whatsapp-controls">
                        <Select
                          options={companies.map(firma => ({ value: firma.id, label: firma.name }))}
                          value={item.value ? { value: item.value, label: companies.find(f => f.id === item.value)?.name } : null}
                          onChange={(option) => item.setter(option ? option.value : '')}
                          className="firma-select"
                          isDisabled={isLoading}
                          isClearable
                          isSearchable
                          placeholder={of('placeholders.selectCompany')}
                          styles={whatsappSelectStyles}
                        />
                        {renderWhatsappLanguageSelect(Boolean(item.value))}
                        <button
                          type="button"
                          onClick={() => handleKatlamaliWhatsAppSend(item.id)}
                          disabled={!item.value || isLoading}
                          className="whatsapp-btn"
                          title={of('whatsapp.sendLabelInfo', { label: item.label })}
                        >
                          <i className="fab fa-whatsapp"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                /* Diğer Ürünler için Standart Firma Seçimi */
                <>
                  {lastSentFirma && (
                    <div className="whatsapp-info">
                      <i className="fas fa-check-circle"></i>
                      <span>{of('whatsapp.sentToCompany', { company: lastSentFirma.name })}</span>
                    </div>
                  )}

                  <div className="whatsapp-controls">
                    <Select
                      options={companies.map(firma => ({ value: firma.id, label: firma.name }))}
                      value={selectedFirma ? { value: selectedFirma, label: companies.find(f => f.id === selectedFirma)?.name } : null}
                      onChange={(option) => setSelectedFirma(option ? option.value : '')}
                      className="firma-select"
                      isDisabled={isLoading}
                      isClearable
                      isSearchable
                      placeholder={of('placeholders.selectCompany')}
                      noOptionsMessage={() => of('messages.noCompany')}
                      filterOption={(option, inputValue) => option.label.toLowerCase().includes(inputValue.toLowerCase())}
                      styles={whatsappSelectStyles}
                    />
                    {renderWhatsappLanguageSelect(Boolean(selectedFirma))}
                    <button
                      type="button"
                      onClick={handleWhatsAppSend}
                      disabled={!selectedFirma || isLoading || !selectedProductType}
                      className="whatsapp-btn"
                      title={of('titles.sendWhatsapp')}
                    >
                      <i className="fab fa-whatsapp"></i>
                      <span>{of('whatsapp.send')}</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </form >
    </div >
  );
};

export default OrderForm; 